<template>
  <section class="ckeditor-component">
    <!-- 自定義右鍵選單 -->
    <div
      v-if="contextMenuVisible"
      class="context-menu"
      :style="{ top: contextMenuY + 'px', left: contextMenuX + 'px' }"
      @click.stop
    >
      <!-- 全選 -->
      <div class="context-menu-item" @click="selectAllText">
        <i class="fa fa-expand"></i>
        <span>全選 Ctrl+A</span>
      </div>

      <!-- 剪下（作答模式、管理者模式） -->
      <div
        v-if="editorMode === 'answer' || editorMode === 'admin'"
        class="context-menu-item"
        @click="cutTextFromContextMenu"
      >
        <i class="fa fa-cut"></i>
        <span>剪下 Ctrl+X</span>
      </div>

      <!-- 複製（所有模式） -->
      <div class="context-menu-item" @click="copyTextFromContextMenu">
        <i class="fa fa-copy"></i>
        <span>複製 Ctrl+C</span>
      </div>

      <!-- 貼上（作答模式、管理者模式） -->
      <div
        v-if="editorMode === 'answer' || editorMode === 'admin'"
        class="context-menu-item"
        @click="pasteTextFromContextMenu"
      >
        <i class="fa fa-paste"></i>
        <span>貼上 Ctrl+V</span>
      </div>

      <!-- 分隔線 -->
      <div
        v-if="editorMode === 'review' || editorMode === 'admin'"
        class="context-menu-divider"
      ></div>

      <!-- 批改（批改模式、管理者模式） -->
      <div
        v-if="editorMode === 'review' || editorMode === 'admin'"
        class="context-menu-item"
        @click="addAnnotationFromContextMenu"
      >
        <i class="fa fa-comment"></i>
        <span>批改 Ctrl+M</span>
      </div>

      <!-- 列表屬性設置（在有序或無序列表中顯示）（作答模式、管理者模式） -->
      <template v-if="editorMode === 'answer' || editorMode === 'admin'">
        <!-- 分隔線 -->
        <div v-if="isInListContext" class="context-menu-divider"></div>

        <div v-if="isInListContext" class="context-menu-item" @click="openListPropertiesDialog">
          <i class="fa fa-list"></i>
          <span>{{ currentListType === 'ol' ? '編號清單屬性' : '項目符號清單屬性' }}</span>
        </div>
      </template>
    </div>

    <!-- 批改彈窗 -->
    <el-dialog
      :visible.sync="annotationDialogVisible"
      :title="editingAnnotationIndex !== null ? '編輯批改' : '新增批改'"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="dialog-content">
        <p class="selected-text-label">選中文字：</p>
        <div class="selected-text-box">{{ selectedTextForAnnotation }}</div>

        <p class="selected-text-label">批改內容：</p>
        <el-input
          v-if="editorMode === 'review' || editorMode === 'admin'"
          v-model="annotationComment"
          type="textarea"
          :rows="4"
          placeholder="請輸入批改內容..."
          maxlength="500"
          show-word-limit
        />
        <div v-else class="selected-text-box">{{ annotationComment }}</div>
      </div>
      <template #footer>
        <el-button type="primary" @click="annotationDialogVisible = false">關閉</el-button>
      </template>
    </el-dialog>

    <!-- HTML編輯彈窗 -->
    <el-dialog
      :visible.sync="htmlDialogVisible"
      title="編輯HTML內容"
      width="700px"
      :close-on-click-modal="false"
    >
      <div class="dialog-content">
        <el-input
          v-model="htmlContent"
          type="textarea"
          :rows="15"
          placeholder="請輸入HTML內容..."
        />
      </div>
      <template #footer>
        <el-button @click="htmlDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmHTMLEdit">確定</el-button>
      </template>
    </el-dialog>

    <!-- 列表屬性設置對話框 -->
    <el-dialog
      :visible.sync="listPropertiesDialogVisible"
      :title="currentListType === 'ol' ? '編號清單屬性' : '項目符號清單屬性'"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="dialog-content list-properties-dialog">
        <!-- 起始序號（僅有序列表顯示） -->
        <div v-if="currentListType === 'ol'" class="form-group">
          <label>開始：</label>
          <el-input-number
            v-model="listStartIndex"
            :min="1"
            :max="999"
            placeholder="請輸入起始序號"
            style="width: 100%"
          />
          <div class="hint-text">設置列表從哪個數字開始計數</div>
        </div>
        <!-- 列表樣式選擇 -->
        <div class="form-group" style="margin-top: 20px">
          <label>類型：</label>
          <el-select v-model="listStyle" placeholder="請選擇列表類型" style="width: 100%">
            <!-- 有序列表樣式 -->
            <el-option-group v-if="currentListType === 'ol'" label="有序列表類型">
              <el-option label="中文數字 一、二、三" value="cjk-ideographic" />
              <el-option label="括號中文數字 (一)(二)(三)" value="parentheses-chinese" />
              <el-option label="數字 1、2、3" value="decimal" />
              <el-option label="括號數字 (1)(2)(3)" value="parentheses" />
            </el-option-group>

            <!-- 無序列表樣式 -->
            <el-option-group v-if="currentListType === 'ul'" label="無序列表類型">
              <el-option label="實心圓點 ●" value="disc" />
              <el-option label="空心圓圈 ○" value="circle" />
              <el-option label="實心方塊 ■" value="square" />
            </el-option-group>
          </el-select>
        </div>

        <!-- 預覽區域 -->
        <div class="preview-section" style="margin-top: 20px">
          <label>預覽效果：</label>
          <div class="preview-box">
            <ol
              v-if="currentListType === 'ol'"
              :class="{
                'parentheses-list': listStyle === 'parentheses',
                'parentheses-chinese-list': listStyle === 'parentheses-chinese',
                'cjk-ideographic-list': listStyle === 'cjk-ideographic'
              }"
              :style="getPreviewStyle()"
              :start="listStartIndex"
            >
              <li>第一項</li>
              <li>第二項</li>
              <li>第三項</li>
            </ol>
            <ul v-else :style="`list-style-type: ${listStyle}; padding-left: 2em;`">
              <li>第一項</li>
              <li>第二項</li>
              <li>第三項</li>
            </ul>
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="listPropertiesDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSetListProperties">確定</el-button>
      </template>
    </el-dialog>

    <div class="ckeditor-wrapper">
      <div v-if="!isReadOnlyMode && editorMode !== 'answer'" class="toolbar">
        <button
          v-if="editorMode === 'admin'"
          @click="editHTML(true)"
          class="btn btn-sm btn-html"
          title="編輯HTML (僅管理者模式)"
        >
          <i class="fa fa-code"></i> HTML
        </button>
        <span v-if="editorMode === 'admin'" class="toolbar-divider"></span>

        <button
          v-if="editorMode === 'review' || editorMode === 'admin'"
          @click="addAnnotation"
          class="btn btn-sm btn-annotation"
          title="新增批改"
        >
          <i class="fa fa-comment"></i> 批改
        </button>
        <span
          v-if="editorMode === 'review' || editorMode === 'admin'"
          class="toolbar-divider"
        ></span>
        <button
          @click="insertPunctuation('，')"
          class="btn btn-sm"
          title="逗號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ，
        </button>
        <button
          @click="insertPunctuation('。')"
          class="btn btn-sm"
          title="句號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          。
        </button>
        <button
          @click="insertPunctuation('、')"
          class="btn btn-sm"
          title="頓號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          、
        </button>
        <button
          @click="insertPunctuation('；')"
          class="btn btn-sm"
          title="分號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ；
        </button>
        <button
          @click="insertPunctuation('：')"
          class="btn btn-sm"
          title="冒號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ：
        </button>
        <button
          @click="insertPunctuation('！')"
          class="btn btn-sm"
          title="驚嘆號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ！
        </button>
        <button
          @click="insertPunctuation('？')"
          class="btn btn-sm"
          title="問號"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ？
        </button>
        <button
          @click="insertPunctuation('－')"
          class="btn btn-sm"
          title="ˇ"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          －
        </button>
        <button
          @click="insertPunctuation('○')"
          class="btn btn-sm"
          title="○"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ○
        </button>
        <!-- <button
          @click="insertPunctuation('※')"
          class="btn btn-sm"
          title="※"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          ※
        </button> -->
        <button
          @click="insertPunctuation('（）')"
          class="btn btn-sm"
          title="（）"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          （）
        </button>
        <button
          @click="insertPunctuation('「」')"
          class="btn btn-sm"
          title="「」"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          「」
        </button>
        <button
          @click="insertPunctuation('『』')"
          class="btn btn-sm"
          title="『』"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          『』
        </button>
        <button
          @click="insertPunctuation('§')"
          class="btn btn-sm"
          title="§"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          §
        </button>
        <button
          @click="insertPunctuation('…')"
          class="btn btn-sm"
          title="…"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          …
        </button>
        <span class="toolbar-divider"></span>

        <!-- 剪貼簿操作 -->
        <button
          @click="cutText"
          class="btn btn-sm"
          title="剪下"
          :disabled="editorMode === 'review' || editorMode === 'readonly' || !hasSelection"
        >
          <i class="fa fa-cut"></i> 剪下
        </button>
        <button @click="copyText" class="btn btn-sm" title="複製" :disabled="!hasSelection">
          <i class="fa fa-copy"></i> 複製
        </button>
        <button
          @click="pasteText"
          class="btn btn-sm"
          title="貼上"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          <i class="fa fa-paste"></i> 貼上
        </button>
        <span class="toolbar-divider"></span>

        <!-- 撤銷/重做 -->
        <button
          @click="undo"
          class="btn btn-sm"
          title="復原"
          :disabled="editorMode === 'review' || editorMode === 'readonly' || !canUndo"
        >
          <i class="fa fa-undo"></i> 復原
        </button>
        <button
          @click="redo"
          class="btn btn-sm"
          title="重做"
          :disabled="editorMode === 'review' || editorMode === 'readonly' || !canRedo"
        >
          <i class="fa fa-repeat"></i> 重做
        </button>
        <span class="toolbar-divider"></span>

        <!-- 列表 -->
        <!-- <button
          @click="insertBulletedList"
          class="btn btn-sm"
          title="無序列表"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          <i class="fa fa-list-ul"></i> 無序列表
        </button> -->
        <button
          @click="insertNumberedList"
          class="btn btn-sm"
          title="有序列表"
          :disabled="editorMode === 'review' || editorMode === 'readonly'"
        >
          <i class="fa fa-list-ol"></i> 有序列表
        </button>

        <span class="toolbar-divider"></span>

        <!-- 縮排 -->
        <button
          @click="decreaseIndent"
          class="btn btn-sm"
          title="減少縮排"
          :disabled="editorMode === 'review' || editorMode === 'readonly' || !canDecreaseIndent"
        >
          <i class="fa fa-indent"></i> 減少縮排
        </button>
        <button
          @click="increaseIndent"
          class="btn btn-sm"
          title="增加縮排"
          :disabled="editorMode === 'review' || editorMode === 'readonly' || !isInList"
        >
          <i class="fa fa-indent"></i> 增加縮排
        </button>
      </div>

      <div class="main-content-wrapper">
        <div class="editor-section">
          <div class="editor-wrapper">
            <div class="word-count-container">
              <button
                v-if="editorMode === 'review' || editorMode === 'admin'"
                @click="addAnnotation"
                class="btn btn-sm btn-annotation"
                title="新增批改"
              >
                <i class="fa fa-comment"></i> 批改
              </button>

              <button
                v-if="false"
                @click="toggleNoteLines"
                class="btn btn-sm btn-note-lines"
                :class="{ 'active': showNoteLines }"
                :title="showNoteLines ? '隱藏稿紙格線' : '顯示稿紙格線'"
              >
                <i class="fa" :class="showNoteLines ? 'fa-th' : 'fa-th-large'"></i>
                {{ showNoteLines ? '隱藏格線' : '顯示格線' }}
              </button>

              <button
                v-if="false"
                @click="toggleListDepthLimit"
                class="btn btn-sm btn-list-limit"
                :class="{ 'active': enableListDepthLimit }"
                :title="enableListDepthLimit ? '已限制最多4層' : '允許無限層級'"
              >
                <i class="fa" :class="enableListDepthLimit ? 'fa-lock' : 'fa-unlock'"></i>
                {{ enableListDepthLimit ? '限制4層' : '無限層級' }}
              </button>

              <div class="word-count-info">
                <span class="count-label">字數統計：</span>
                <span
                  class="count-value"
                  :class="{ 'text-warning': isWordLimitClose, 'text-danger': isWordLimitExceeded }"
                >
                  {{ characterCount }} / {{ maxWordCount }} 字 (計 {{ CurrentQuestionLimitPage }} 頁)
                </span>
              </div>
            </div>
            <div
              ref="editorElement"
              class="custom-editor"
              :class="{ 'limit-exceeded': isWordLimitExceeded, 'limit-close': isWordLimitClose }"
            ></div>
            <div class="word-count-container bottom">
              <div class="word-count-info">
                <span class="count-label">字數統計：</span>
                <span
                  class="count-value"
                  :class="{ 'text-warning': isWordLimitClose, 'text-danger': isWordLimitExceeded }"
                >
                  {{ characterCount }} / {{ maxWordCount }} 字 (計 {{ CurrentQuestionLimitPage }} 頁)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="annotations-section"
          v-if="annotations.length > 0"
          :class="{ collapsed: isAnnotationsCollapsed }"
        >
          <button
            @click="toggleAnnotations"
            class="panel-tab"
            :class="{ expanded: !isAnnotationsCollapsed }"
            :title="isAnnotationsCollapsed ? '展開批改' : '收起批改'"
          >
            <span class="tab-text">批改列表</span>
          </button>
          <div class="annotations-panel" v-show="!isAnnotationsCollapsed">
            <div class="annotations-header">
              <h6>批改列表 ({{ annotations.length }})</h6>
              <button
                @click="toggleAnnotationHighlight"
                class="btn-toggle-highlight"
                :class="{ active: showAnnotationHighlight }"
                :title="showAnnotationHighlight ? '隱藏批改標記' : '顯示批改標記'"
              >
                <i class="mdi" :class="showAnnotationHighlight ? 'mdi-eye' : 'mdi-eye-off'"></i>
                {{ showAnnotationHighlight ? '隱藏標記' : '顯示標記' }}
              </button>
            </div>
            <div class="annotations-list">
              <div
                v-for="(item, index) in annotations"
                :key="index"
                class="annotation-item"
                :draggable="editorMode === 'review' || editorMode === 'admin'"
                :class="{
                  dragging: draggingIndex === index,
                  'drag-over': dragOverIndex === index,
                  'can-drag-drop': editorMode === 'review' || editorMode === 'admin'
                }"
              >
                <!-- 序號 -->
                <div class="annotation-number">{{ index + 1 }}</div>

                <!-- 批改內容 -->
                <div
                  class="annotation-content"
                  @click="jumpToAnnotation(item)"
                  style="cursor: pointer"
                >
                  <!-- 選取的文字 -->
                  <div class="annotation-text">{{ item.selectedText }}</div>
                  <!-- 老師的評論 -->
                  <div class="annotation-comment">{{ item.teacherComment }}</div>
                </div>

                <!-- 檢視按鈕 -->
                <template v-if="editorMode === 'readonly'">
                  <button
                    @click="editAnnotation(item)"
                    class="btn-view"
                    title="檢視"
                  >
                    <i class="fa fa-eye"></i>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CkeditorComponent',
  props: {
    initialHtml: { type: String, default: '' },
    initialMode: {
      type: String,
      default: 'answer',
      validator: (value) => ['answer', 'review', 'readonly', 'admin'].includes(value),
    },
    initialAnnotations: { type: Array, default: () => [] },
    maxWordCount: { type: Number, default: 5200 },
    studentEssayRecordId: { type: Number, default: 0 },
    essayQuestionsId: { type: Number, default: 0 },
  },
  data() {
    return {
      editorInstance: null,
      editorData: this.initialHtml || '',
      editorMode: this.initialMode,
      annotations: [],
      annotationDialogVisible: false,
      selectedTextForAnnotation: '',
      annotationComment: '',
      editingAnnotationIndex: null,
      htmlDialogVisible: false,
      htmlContent: '',
      isAnnotationsCollapsed: false,
      draggingIndex: null,
      dragOverIndex: null,
      showAnnotationHighlight: true,
      currentAnnotationRange: null,
      wordCount: 0,
      characterCount: 0,
      hasSelection: false,
      isInList: false,
      canDecreaseIndent: false,
      canUndo: false,
      canRedo: false,
      contextMenuVisible: false,
      contextMenuX: 0,
      contextMenuY: 0,
      savedSelection: null,
      isInListContext: false,
      currentListType: 'ol',
      listPropertiesDialogVisible: false,
      listStartIndex: 1,
      listStyle: 'decimal',
      currentListElement: null,
      showNoteLines: true, // 稿紙格線顯示開關（預設顯示）
      pageMarkersUpdateTimer: null, // 頁碼標記更新計時器
      enableListDepthLimit: true, // 列表層級限制開關（預設開啟，最多4層）
      hasShownWordLimitWarning: false, // 是否已顯示過字數超限警告
      isEditorInitialized: false, // 編輯器初始化是否完成
      hasShownPageLimitWarning: false, // 是否已顯示過「達最大頁數」警告
      hasShownPageExceededWarning: false, // 是否已顯示過「超過最大頁數」警告
      isPageMarkersInitialized: false, // 頁碼標記初始化是否完成
    };
  },
  computed: {
    isWordLimitExceeded() {
      return this.characterCount > this.maxWordCount;
    },
    isWordLimitClose() {
      return !this.isWordLimitExceeded && this.characterCount > this.maxWordCount * 0.8;
    },
    isReadOnlyMode() {
      return this.editorMode === 'readonly' || this.editorMode === 'review';
    },
    CurrentQuestionLimitPage() {
      return Math.ceil(this.maxWordCount / (25 * 26));
    },
  },
  mounted() {
    // 先設置初始資料
    if (this.initialHtml) {
      this.editorData = this.initialHtml;
    }

    // 載入初始批改資料
    if (this.initialAnnotations && this.initialAnnotations.length > 0) {
      this.annotations = this.initialAnnotations.map((ann) => ({
        ...ann,
        studentEssayAnswerAnnotationId: ann.studentEssayAnswerAnnotationId,
      }));
    }

    this.initCKEditor();
    document.addEventListener('click', this.hideContextMenu);
    document.addEventListener('keydown', this.handleKeyboardShortcut);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.hideContextMenu);
    document.removeEventListener('keydown', this.handleKeyboardShortcut);
    if (this.editorInstance) {
      this.editorInstance.destroy();
      this.editorInstance = null;
    }
  },
  methods: {
    initCKEditor() {
      if (!this.$refs.editorElement) {
        // console.error('editorElement ref not found!');
        return;
      }

      if (typeof window.CKEDITOR === 'undefined') {
        const script = document.createElement('script');
        // 使用本地路徑載入 CKEditor
        script.src = 'https://cdn.ckeditor.com/4.22.1/standard/ckeditor.js';
        script.onload = () => {
          this.registerPlugins();
          this.createEditor();
        };
        // script.onerror = (error) => {
        //   console.error('Failed to load CKEditor:', error);
        // };
        document.head.appendChild(script);
      } else {
        this.registerPlugins();
        this.createEditor();
      }
    },
    registerPlugins() {
      const vm = this;

      // 註冊中文標點符號按鈕插件
      if (!window.CKEDITOR.plugins.registered.punctuationchars) {
        window.CKEDITOR.plugins.add('punctuationchars', {
          init(editor) {
            const chars = [
              { name: 'CJKComma', char: '，', label: '，(逗號)' },
              { name: 'CJKPeriod', char: '。', label: '。(句號)' },
              { name: 'CJKDunhao', char: '、', label: '、(頓號)' },
              { name: 'CJKSemicolon', char: '；', label: '；(分號)' },
              { name: 'CJKColon', char: '：', label: '：(冒號)' },
              { name: 'CJKExclam', char: '！', label: '！(驚嘆號)' },
              { name: 'CJKQuestion', char: '？', label: '？(問號)' },
              { name: 'CJKDash', char: '－', label: '－' },
              { name: 'Circle', char: '○', label: '○(空心圓)' },
              // { name: 'Mark', char: '※', label: '※' },
              { name: 'CJKParen', char: '（）', label: '（）(括號)' },
              { name: 'CJKQuote', char: '「」', label: '「」(右引號)' },
              { name: 'CJKDQuote', char: '『』', label: '『』(雙引號)' },
              { name: 'SectionSign', char: '§', label: '§' },
              { name: 'Ellipsis', char: '…', label: '…' },
            ];

            chars.forEach((item, idx) => {
              const cmdName = `insert_${item.name}`;

              // 定義插入字元的 command
              editor.addCommand(cmdName, {
                exec(ed) {
                  ed.insertText(item.char);
                },
              });

              // 定義 toolbar 上的按鈕
              editor.ui.addButton(item.name, {
                label: item.char, // hover 提示文字
                command: cmdName,
                toolbar: `insert,${10 + idx}`,
              });
            });
          },
        });
      }

      // 註冊批改按鈕插件
      if (!window.CKEDITOR.plugins.registered.annotationButton) {
        window.CKEDITOR.plugins.add('annotationButton', {
          init(editor) {
            editor.addCommand('addAnnotation', {
              exec() {
                vm.addAnnotation();
              },
            });

            editor.ui.addButton('Annotation', {
              label: '批改',
              title: '批改 (Ctrl+M)',
              command: 'addAnnotation',
              toolbar: 'custom',
            });

            // 註冊 Ctrl+M 快捷鍵
            editor.setKeystroke(window.CKEDITOR.CTRL + 77, 'addAnnotation'); // 77 是 'M' 的鍵碼
          },
        });
      }

      // 註冊貼上按鈕功能
      if (!window.CKEDITOR.plugins.registered.custompaste) {
        window.CKEDITOR.plugins.add('custompaste', {
          icons: 'paste', // 使用 CKEditor 內建 paste 圖示
          init(editor) {
            editor.addCommand('customPaste', {
            // eslint-disable-next-line no-shadow
              exec(editor) {
                if (navigator.clipboard && navigator.clipboard.readText) {
                  navigator.clipboard.readText().then((clipText) => {
                    if (clipText) {
                      editor.insertText(clipText);
                      if (typeof showAlert === 'function') {
                      // showAlert('已貼上剪貼簿內容', { icon: 'success' });
                      } else {
                      // alert('已貼上剪貼簿內容');
                      }
                    } else {
                    // alert('剪貼簿沒有內容');
                    }
                  }, () => {
                  // alert('貼上失敗，請手動 Ctrl+V');
                  });
                } else {
                // alert('瀏覽器不支援自動貼上，請手動 Ctrl+V');
                }
              },
            });
            editor.ui.addButton('CustomPaste', {
              label: '貼上 (Ctrl+V)',
              command: 'customPaste',
              icon: 'paste',
            });
          },
        });
      }
    },
    createEditor() {
      const vm = this;

      // ========== 定義編輯器尺寸常數 ==========
      const CHARS_PER_LINE = 25; // 每行字數
      const FONT_SIZE = 24; // 字體大小 (px)
      const LINE_HEIGHT = 36; // 行高 (px)
      const LETTER_SPACING = 4; // 字元間距 (px)
      // 25 * (24 + 4) = 700px
      // 編輯器寬度 (px)，加上左右 padding 和邊框
      const EDITOR_WIDTH = CHARS_PER_LINE * (FONT_SIZE + LETTER_SPACING) + LETTER_SPACING;

      // 創建自訂樣式表內容
      const customStyles = `
        .cke_editable {
          font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif !important;
          font-size: ${FONT_SIZE}px;
          line-height: ${LINE_HEIGHT}px;
          letter-spacing: ${LETTER_SPACING}px;
          /* 設定編輯器寬度為 ${CHARS_PER_LINE} 字寬（含 letter-spacing ${LETTER_SPACING}px） */
          max-width: ${EDITOR_WIDTH}px;
          margin: 0 auto;
          position: relative;
          padding: 2px;
          border: 2px #0573D7 solid;
          min-height:100vh;
          padding-left: 10px;
          padding-right: 10px;
          word-wrap: break-word;
        }

        .cke_editable * {
          font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif !important;
          font-size: ${FONT_SIZE}px !important;
          letter-spacing: ${LETTER_SPACING}px !important;
        }

        /* 格線模式下強制 line-height */
        .cke_editable.note-lines * {
          line-height: ${LINE_HEIGHT}px !important;
        }

        /* 重置 標籤的 margin 和 padding，避免影響格線對齊 */
        .cke_editable > p , .cke_editable  li , .cke_editable  ol , .cke_editable  ul{
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        /* ========== 稿紙格線樣式（僅套用在 CKEditor 編輯區） ========== */

        /* 顯示格線 */
        .cke_editable.note-lines {
          line-height: ${LINE_HEIGHT}px;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='${EDITOR_WIDTH}' height='${LINE_HEIGHT}'><line x1='0' y1='${LINE_HEIGHT - 1}' x2='${EDITOR_WIDTH}' y2='${LINE_HEIGHT - 1}' stroke='%230573d7' stroke-width='1'/></svg>");
          background-repeat: repeat-y;
          background-size: ${EDITOR_WIDTH + 20}px ${LINE_HEIGHT}px;
          background-attachment: local;
          background-position: center 0;
          padding-top: 0 !important;
          margin-top: 0 !important;
        }

        /* 隱藏格線 */
        .cke_editable.note-lines-hidden {
          line-height: ${LINE_HEIGHT}px;
          background-image: none !important;
        }

        /* ========== 標準數字列表 ========== */
        ol {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;
        }

        ol > li {
          --mk: 1.5em;
          counter-increment: item;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px;
          /* margin-bottom: 0.5em; */
          word-break: break-all;
        }

        ol > li::before {
          content: counter(item) '.';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px;
        }

        /* 子層列表處理 */
        ol > li > ol,
        ol > li > ul {
          display: table-caption;
          caption-side: bottom;
         /* margin-top: 0.5em;*/
          padding-left: var(--mk) !important;
          word-break: break-all;
        }

        /* ========== 括號數字列表 (1), (2), (3) ========== */
        ol.parentheses-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;
        }

        ol.parentheses-list > li {
          --mk: 2em;
          counter-increment: item;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px;
         /* margin-bottom: 0.5em; */
          word-break: break-all;
        }

        ol.parentheses-list > li::before {
          content: '(' counter(item) ')';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px;
        }

        ol.parentheses-list > li > ol,
        ol.parentheses-list > li > ul {
          display: table-caption;
          caption-side: bottom;
         /* margin-top: 0.5em;*/
          padding-left: var(--mk) !important;
          word-break: break-all;
        }

        /* ========== 括號中文數字列表 (一)、(二)、(三) ========== */
        ol.parentheses-chinese-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;
        }

        ol.parentheses-chinese-list > li {
          --mk: 2em;
          counter-increment: item;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px;
         /* margin-bottom: 0.5em; */
          word-break: break-all;
        }

        ol.parentheses-chinese-list > li::before {
          content: '(' counter(item, cjk-ideographic) ')';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px;
        }

        ol.parentheses-chinese-list > li > ol,
        ol.parentheses-chinese-list > li > ul {
          display: table-caption;
          caption-side: bottom;
         /* margin-top: 0.5em; */
          padding-left: var(--mk) !important;
          word-break: break-all;
        }

        /* ========== 中文數字列表 一、二、三 ========== */
        ol.cjk-ideographic-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;
        }

        ol.cjk-ideographic-list > li {
          --mk: 1.5em;
          counter-increment: item;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px;
         /* margin-bottom: 0.5em;*/
          word-break: break-all;
        }

        ol.cjk-ideographic-list > li::before {
          content: counter(item, cjk-ideographic) '、';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px;
        }

        ol.cjk-ideographic-list > li > ol,
        ol.cjk-ideographic-list > li > ul {
          display: table-caption;
          caption-side: bottom;
          /* margin-top: 0.5em;*/
          padding-left: var(--mk) !important;
          word-break: break-all;
        }

        /* ========== 無序列表 ========== */
        ul {
          padding-left: 1.5em !important;
        }

        ul > li {
          line-height: 36px;
         /* margin-bottom: 0.5em; */
          padding-left: 1em !important;
          word-break: break-all;
        }

        ul > li > ol,
        ul > li > ul {
         /* margin-top: 0.5em; */
          padding-left: 1em !important;
        }

        /* ========== 批改標記樣式 ========== */
        mark.annotation-highlight {
          display: inline;
          background-color: #ffff00;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        mark.annotation-highlight.hidden {
          background-color: transparent !important;
          cursor: inherit;
        }

        /*
         * annotation-jump-active
         * ──────────────────────────────────────────────────────────────────
         * 點擊「跳轉至批改」時，暫時套用此 class 產生閃爍高亮動畫。
         * 刻意使用更高優先度（mark.annotation-highlight.annotation-jump-active）
         * 且加上 !important，確保能覆蓋 .hidden 狀態的 transparent !important，
         * 讓使用者無論批改標記是否顯示中，都能看到跳轉定位的視覺回饋。
         * 此 class 由 jumpToAnnotation() 動態加上，約 1000ms 後自動移除。
         */
        mark.annotation-highlight.annotation-jump-active {
          background-color: #ff6b6b !important;
          outline: 2px solid #e74c3c;
          transition: background-color 0.3s ease;
        }

        /* ========== 頁碼標記樣式 ========== */
        /* 每26行（936px）顯示分隔線和頁碼 */
        .page-marker {
          position: absolute;
          left: 0;
          right: 0;
          height: 0;
          z-index: 10;
          pointer-events: none;
        }

        .page-line {
          border-top: 2px dashed #0573D7;
          width: 100%;
        }

        .page-number {
          position: absolute;
          left: -100px;
          top: -12px;
          color: #0573D7;
        }
      `;

      // 根據模式動態設置 toolbar 配置
      const toolbarConfig = [];

      // 只有管理者模式才顯示原始碼按鈕
      if (vm.editorMode === 'admin') {
        toolbarConfig.push({ name: 'document', items: ['Source'] });
      }

      // 只有批改模式和管理者模式才顯示批改按鈕
      if (vm.editorMode === 'review' || vm.editorMode === 'admin') {
        toolbarConfig.push({ name: 'custom', items: ['Annotation'] });
      }
      // 其他工具列項目
      toolbarConfig.push(
        {
          name: 'insert',
          items: [
            'CJKComma',
            'CJKPeriod',
            'CJKDunhao',
            'CJKSemicolon',
            'CJKColon',
            'CJKExclam',
            'CJKQuestion',
            'CJKDash',
            'Circle',
            'Mark',
            'CJKParen',
            'CJKQuote',
            'CJKDQuote',
            'SectionSign',
            'Ellipsis',
          ],
        },
        { name: 'clipboard', items: ['Cut', 'Copy', 'CustomPaste'] },
        { name: 'editing', items: ['Undo', 'Redo'] },
        // 含無序列表（暫時關閉）
        // { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'] },
        { name: 'paragraph', items: ['NumberedList', '-', 'Outdent', 'Indent'] },
      );

      const config = {
        language: 'zh',
        placeholder: '請在此輸入內容...',
        removePlugins: 'elementspath,contextmenu,tabletools,tableselection,liststyle',
        resize_enabled: false,
        height: 525,
        allowedContent: true,
        extraAllowedContent: 'mark[*]{*}(*)',
        versionCheck: false,
        contentsCss: [`data:text/css;charset=utf-8,${encodeURIComponent(customStyles)}`],
        // 啟用自訂外掛
        extraPlugins: 'punctuationchars,annotationButton,custompaste,indent,indentlist',
        // 配置縮排
        indentOffset: 40,
        indentUnit: 'px',
        // 配置 toolbar（根據模式動態設置）
        toolbar: this.isReadOnlyMode ? [] : toolbarConfig,
        // 配置 bodyClass（根據格線狀態動態設置）
        bodyClass: vm.showNoteLines ? 'note-lines' : 'note-lines-hidden',
        // 換行模式設定：使用 BR 而非 P // Enter 鍵使用 <br>
        enterMode: window.CKEDITOR.ENTER_BR,
        // Shift+Enter 使用 <p>
        // 防止自動生成 <p> 標籤
        shiftEnterMode: window.CKEDITOR.ENTER_BR,
        // 不自動將內容包在 <p> 中
        autoParagraph: false,
        // 不自動填充空區塊
        fillEmptyBlocks: false,
        undoStackSize: 2, // 限制歷史紀錄步數
        on: {
          instanceReady: (evt) => {
            vm.editorInstance = evt.editor;

            // 覆寫 CKEditor toolbar 按鈕的 title，修正重複「清單」問題
            // 注意：instanceReady 時 DOM 已渲染，需直接操作 DOM 才有效
            const { editor } = evt;
            const editorContainer = editor.container && editor.container.$;
            if (editorContainer) {
              const numberedListBtn = editorContainer.querySelector('.cke_button__numberedlist');
              if (numberedListBtn) {
                numberedListBtn.title = '插入/移除編號清單';
                const label = numberedListBtn.querySelector('.cke_button_label');
                if (label) label.textContent = '插入/移除編號清單';
              }
              const bulletedListBtn = editorContainer.querySelector('.cke_button__bulletedlist');
              if (bulletedListBtn) {
                bulletedListBtn.title = '插入/移除項目符號清單';
                const label = bulletedListBtn.querySelector('.cke_button_label');
                if (label) label.textContent = '插入/移除項目符號清單';
              }
            }

            // 設置初始內容
            if (vm.editorData) {
              vm.editorInstance.setData(vm.editorData);
            }

            vm.updateEditorMode();

            // 等待編輯器完全準備好後再應用批改標記
            vm.$nextTick(() => {
              setTimeout(() => {
                if (vm.annotations && vm.annotations.length > 0) {
                  vm.applyAllAnnotationMarks();
                }
                vm.updateWordCount();
                vm.isEditorInitialized = true;
              }, 300);
            });

            vm.editorInstance.on('change', () => {
              vm.editorData = vm.getCleanHTML(); // 使用乾淨的 HTML（不含頁碼標記）
              vm.updateWordCount();
              vm.schedulePageMarkersUpdate(); // 更新頁碼標記
              vm.$emit('data-changed', { html: vm.editorData });
            });

            // 監聽貼上事件（Ctrl+V）
            vm.editorInstance.on('paste', () => {
              // 延遲處理，確保內容已經貼上並插入到編輯器中
              setTimeout(() => {
                const bodyElement = vm.editorInstance.document.getBody();

                // 第一步：如果開啟層級限制，檢查並調整超過 4 層的列表
                if (vm.enableListDepthLimit) {
                  const allLists = bodyElement.find('ol');
                  for (let i = 0; i < allLists.count(); i += 1) {
                    const list = allLists.getItem(i);
                    const depth = vm.calculateDepth(list);

                    if (depth > 3) {
                      vm.adjustListToMaxDepth(list, 3);
                    }
                  }
                }

                // 第二步：重新獲取所有列表並應用樣式
                const updatedLists = bodyElement.find('ol');
                for (let i = 0; i < updatedLists.count(); i += 1) {
                  const list = updatedLists.getItem(i);
                  if (!vm.hasUserDefinedStyle(list)) {
                    const depth = vm.calculateDepth(list);
                    vm.applyStyleByDepth(list, depth, true);
                  }
                }
              }, 100); // 延遲 100ms 確保內容已插入
            });

            // 初始化頁碼標記
            vm.$nextTick(() => {
              setTimeout(() => {
                vm.updatePageMarkers();
                // 等待 rAF 完成後再標記初始化完成（rAF 約 16ms）
                setTimeout(() => {
                  vm.isPageMarkersInitialized = true;
                }, 50);
              }, 500);
            });

            // 新增自訂右鍵選單項目
            if (vm.editorInstance.addMenuGroup) {
              vm.editorInstance.addMenuGroup('customGroup', 100);
            }

            // 新增批改選單項目
            if (
              (vm.editorMode === 'review' || vm.editorMode === 'admin')
              && vm.editorInstance.addMenuItem
            ) {
              vm.editorInstance.addMenuItem('addAnnotation', {
                label: '批改 (Ctrl+M)',
                command: 'addAnnotation',
                group: 'customGroup',
                icon: null,
                order: 1,
              });
            }

            // 綁定右鍵選單事件和快捷鍵（只綁定一次）
            let contextMenuBound = false;
            vm.editorInstance.on('contentDom', () => {
              if (contextMenuBound) return; // 避免重複綁定
              contextMenuBound = true;

              const editable = vm.editorInstance.editable();
              const editableElement = editable.$;

              // 使用多種方式阻止原生選單並觸發自訂選單
              const preventContextMenu = (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                // vm.handleContextMenu(e); // 暫時關閉右鍵選單
                return false;
              };

              // 方法1: 使用 oncontextmenu
              editableElement.oncontextmenu = preventContextMenu;

              // 方法2: 使用 addEventListener（捕獲階段）
              editableElement.addEventListener('contextmenu', preventContextMenu, true);

              // 監聽 Ctrl+M 快捷鍵（特別針對批改模式）
              editable.attachListener(editable, 'keydown', (event) => {
                const domEvent = event.data.$;
                if ((domEvent.ctrlKey || domEvent.metaKey) && domEvent.key === 'm') {
                  domEvent.preventDefault();
                  domEvent.stopPropagation();
                  if (vm.editorMode === 'review' || vm.editorMode === 'admin') {
                    vm.addAnnotation();
                  } else {
                    vm.$message.warning('批改功能僅在批改模式或管理者模式下可用！');
                  }
                }
              });
            });

            // 監聽命令執行前，檢查層級限制
            vm.editorInstance.on('beforeCommandExec', (cmdEvt) => {
              const commandName = cmdEvt.data.name;

              // 對於 indent 命令，在執行前檢查層級限制
              if (commandName === 'indent' && vm.enableListDepthLimit) {
                const selection = vm.editorInstance.getSelection();
                if (selection) {
                  const startElement = selection.getStartElement();
                  const currentLi = startElement.getAscendant('li', true);

                  if (currentLi) {
                    // 計算當前 li 所在的列表深度
                    let depth = 0;
                    let element = currentLi.getParent();

                    while (element) {
                      const tagName = element.getName();
                      if (tagName === 'ol' || tagName === 'ul') {
                        depth += 1;
                      }
                      element = element.getParent();
                    }

                    // 如果已經是第4層，阻止命令執行
                    if (depth >= 4) {
                      cmdEvt.cancel();
                      vm.$message.warning('已達到最大層級限制（4層）');
                    }
                  }
                }
              }
            });

            // 監聽命令執行完成，自動應用樣式
            vm.editorInstance.on('afterCommandExec', (cmdEvt) => {
              const commandName = cmdEvt.data.name;

              // 對於 indent 命令且開啟層級限制，需要檢查並調整超限列表
              if (commandName === 'indent' && vm.enableListDepthLimit) {
                vm.$nextTick(() => {
                  const bodyElement = vm.editorInstance.document.getBody();
                  const allLists = bodyElement.find('ol');

                  // 第一步：找出所有超過第4層的列表並調整回第4層
                  for (let i = 0; i < allLists.count(); i += 1) {
                    const list = allLists.getItem(i);
                    const depth = vm.calculateDepth(list);

                    // 如果深度超過3（即第4層以上），需要調整
                    if (depth > 3) {
                      vm.adjustListToMaxDepth(list, 3);
                    }
                  }

                  // 第二步：重新獲取所有列表並應用樣式
                  const updatedLists = bodyElement.find('ol');
                  for (let i = 0; i < updatedLists.count(); i += 1) {
                    const list = updatedLists.getItem(i);
                    if (!vm.hasUserDefinedStyle(list)) {
                      const depth = vm.calculateDepth(list);
                      vm.applyStyleByDepth(list, depth, true);
                    }
                  }
                });
              } else if (commandName === 'numberedlist' || commandName === 'outdent') {
                // 對於 numberedlist、outdent 命令，只應用樣式
                vm.$nextTick(() => {
                  const bodyElement = vm.editorInstance.document.getBody();
                  const allLists = bodyElement.find('ol');

                  for (let i = 0; i < allLists.count(); i += 1) {
                    const list = allLists.getItem(i);
                    if (!vm.hasUserDefinedStyle(list)) {
                      const depth = vm.calculateDepth(list);
                      vm.applyStyleByDepth(list, depth, true);
                    }
                  }
                });
              } else if (commandName === 'paste') {
                // 對於 paste 命令，需要檢查並調整超限列表，然後應用樣式
                vm.$nextTick(() => {
                  const bodyElement = vm.editorInstance.document.getBody();

                  // 第一步：如果開啟層級限制，檢查並調整超過 4 層的列表
                  if (vm.enableListDepthLimit) {
                    const allLists = bodyElement.find('ol');
                    for (let i = 0; i < allLists.count(); i += 1) {
                      const list = allLists.getItem(i);
                      const depth = vm.calculateDepth(list);

                      if (depth > 3) {
                        vm.adjustListToMaxDepth(list, 3);
                      }
                    }
                  }

                  // 第二步：重新獲取所有列表並應用樣式
                  const updatedLists = bodyElement.find('ol');
                  for (let i = 0; i < updatedLists.count(); i += 1) {
                    const list = updatedLists.getItem(i);
                    if (!vm.hasUserDefinedStyle(list)) {
                      const depth = vm.calculateDepth(list);
                      vm.applyStyleByDepth(list, depth, true);
                    }
                  }
                });
              }
            });

            // 監聽選取變化以更新按鈕狀態
            vm.editorInstance.on('selectionChange', () => {
              vm.updateButtonStates();
            });

            // 初始化按鈕狀態
            vm.$nextTick(() => {
              vm.updateButtonStates();
            });

            // 注入自訂樣式到編輯器
            const doc = (
              vm.editorInstance.document.$
              || vm.editorInstance.document.getWindow().$.document
            );
            if (doc) {
              const styleElement = doc.createElement('style');
              styleElement.type = 'text/css';
              styleElement.innerHTML = customStyles;
              doc.head.appendChild(styleElement);
            }
          },
        },
      };

      // 創建編輯器實例（不在這裡賦值給 this.editorInstance，因為在 instanceReady 中已經賦值）
      try {
        window.CKEDITOR.replace(vm.$refs.editorElement, config);
      } catch (error) {
        // console.error('Error creating CKEditor:', error);
      }
    },
    updateWordCount() {
      if (!this.editorInstance) return;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.editorData;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      this.characterCount = textContent.length - (textContent.match(/\n/g)?.length || textContent.match(/\r\n/g)?.length || 0);
      const cleanedText = textContent.trim();
      if (cleanedText.length === 0) {
        this.wordCount = 0;
        this.$emit('word-count-changed', {
          wordCount: 0,
          characterCount: 0,
          maxWordCount: this.maxWordCount,
          isWordLimitExceeded: false,
          isWordLimitClose: false,
        });
        return;
      }
      const cjkRegex = /[\u4E00-\u9FFF\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/g;
      const cjkMatches = cleanedText.match(cjkRegex) || [];
      const cjkCount = cjkMatches.length;
      const nonCjkText = cleanedText.replace(cjkRegex, ' ').trim();
      const words = nonCjkText.split(/\s+/).filter((word) => word.length > 0);
      const nonCjkCount = words.length;

      this.wordCount = cjkCount + nonCjkCount;

      // 檢查是否超過限制
      if (this.editorMode === 'answer' && this.isEditorInitialized) {
        if (this.characterCount > this.maxWordCount) {
          // 超過上限且尚未顯示過警告，才顯示一次
          if (!this.hasShownWordLimitWarning) {
            this.hasShownWordLimitWarning = true;
            this.$alert(
              `目前字數 ${this.characterCount} 字，上限字數 ${this.maxWordCount} 字，請刪除字數再繼續使用。`,
              '字數已超過限制！',
              {
                confirmButtonText: '我知道了',
                type: 'warning',
                dangerouslyUseHTMLString: true,
              },
            );
          }
        } else {
          // 字數回到上限以下，重置旗標，下次超限再提醒
          this.hasShownWordLimitWarning = false;
        }
      }

      this.$emit('word-count-changed', {
        wordCount: this.wordCount,
        characterCount: this.characterCount,
        maxWordCount: this.maxWordCount,
        isWordLimitExceeded: this.isWordLimitExceeded,
        isWordLimitClose: this.isWordLimitClose,
      });
    },
    insertPunctuation(punctuation) {
      if (!this.editorInstance) return;
      this.editorInstance.insertText(punctuation);
      this.editorInstance.focus();
    },
    updateEditorMode() {
      if (!this.editorInstance) return;
      // 'readonly' 和 'review' 模式都應該設為唯讀；'review' 模式只能新增批改標記，不能編輯內容
      const isReadOnly = this.editorMode === 'readonly' || this.editorMode === 'review';
      this.editorInstance.setReadOnly(isReadOnly);

      // 動態顯示或隱藏 Source 按鈕
      const sourceButton = this.editorInstance.ui.get('Source');
      if (sourceButton) {
        if (this.editorMode === 'admin') {
          // 管理者模式：顯示 Source 按鈕
          sourceButton.setState(window.CKEDITOR.TRISTATE_OFF);
          const buttonElement = document.getElementById(sourceButton._.id);
          if (buttonElement) {
            buttonElement.style.display = '';
          }
        } else {
          // 非管理者模式：隱藏 Source 按鈕
          const buttonElement = document.getElementById(sourceButton._.id);
          if (buttonElement) {
            buttonElement.style.display = 'none';
          }
        }
      }

      // 動態顯示或隱藏 Annotation 按鈕
      const annotationButton = this.editorInstance.ui.get('Annotation');
      if (annotationButton) {
        if (this.editorMode === 'review' || this.editorMode === 'admin') {
          // 批改模式或管理者模式：顯示 Annotation 按鈕
          annotationButton.setState(window.CKEDITOR.TRISTATE_OFF);
          const buttonElement = document.getElementById(annotationButton._.id);
          if (buttonElement) {
            buttonElement.style.display = '';
          }
        } else {
          // 其他模式：隱藏 Annotation 按鈕
          const buttonElement = document.getElementById(annotationButton._.id);
          if (buttonElement) {
            buttonElement.style.display = 'none';
          }
        }
      }
    },
    addAnnotation() {
      if (!this.editorInstance) return;

      // 檢查當前模式，只在批改模式或管理者模式下允許新增批改
      if (this.editorMode !== 'review' && this.editorMode !== 'admin') {
        this.$message.warning('此功能僅在批改模式或管理者模式下可用！');
        return;
      }

      const selection = this.editorInstance.getSelection();
      const selectedText = selection ? selection.getSelectedText().trim() : '';
      if (!selectedText) {
        this.$message.warning('請先選擇要批改的文字！');
        return;
      }
      const ranges = selection.getRanges();
      const [firstRange] = ranges;
      if (ranges.length > 0) this.currentAnnotationRange = firstRange;
      this.selectedTextForAnnotation = selectedText;
      this.annotationComment = '';
      this.annotationDialogVisible = true;
    },
    /**
     * applyAllAnnotationMarks
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：將所有批改標記（<mark>）套用到 CKEditor 的 DOM 上。
     *   透過 TreeWalker 走訪所有文字節點，找出落在 [startIndex, endIndex) 範圍內
     *   的片段，逐一切割並分別包裹 <mark>，因此每個 <p> 內的文字節點都是獨立處理，
     *   不受 block 邊界限制。
     *
     * 【流程示意】
     *   HTML: <p>ABC</p><p>DEF</p>
     *   getText(): "ABCDEF"  ← 連續純文字，無換行
     *   跨段落：startIndex=2, endIndex=6 → 選取 "CDEF"
     *     TreeWalker 節點1: "ABC" → 交集 [2,3) → localStart=2, localEnd=3
     *     TreeWalker 節點2: "DEF" → 交集 [3,6) → localStart=0, localEnd=3
     *     結果: <p>AB<mark>C</mark></p><p><mark>DEF</mark></p>
     *
     * @param {void}
     */
    applyAllAnnotationMarks() {
      if (!this.editorInstance) return;

      const wasReadOnly = this.editorInstance.readOnly;
      if (wasReadOnly) {
        this.editorInstance.setReadOnly(false);
      }

      // 取得編輯器的純文字內容（getText() 會將所有段落文字連接，不含換行）
      const bodyElement = this.editorInstance.document.getBody();
      const fullText = bodyElement.getText();

      this.annotations.forEach((annotation, index) => {
        if (!annotation.selectedText || !annotation.range) {
          return;
        }

        const annotationId = annotation.studentEssayAnswerAnnotationId;
        const { startIndex, endIndex } = annotation.range;

        try {
          // 驗證索引範圍是否合法
          if (startIndex < 0 || endIndex > fullText.length || startIndex >= endIndex) {
            // console.warn(`批改 ${index + 1} 的索引範圍無效`);
            this.findAndMarkTextFallback(annotation.selectedText, annotationId, index);
            return;
          }

          // // 驗證該位置的純文字是否與儲存的 selectedText 一致。
          // // 歷史資料的 selectedText 可能含有 \n（CKEditor selection 在段落/列表間插入），
          // // 但 fullText（getText()）和 startIndex/endIndex 都是以不含 \n 的純文字計算，
          // // 因此比對前必須先正規化，移除 selectedText 中的 \n 再做比較。
          // const textAtPosition = fullText.substring(startIndex, endIndex);
          // const normalizedSelectedText = annotation.selectedText.replace(/\n/g, '');
          // if (textAtPosition !== normalizedSelectedText) {
          //   console.warn(`批改 ${index + 1} 的位置文字不匹配，改用文字搜尋備援`, {
          //     textAtPosition,
          //     normalizedSelectedText,
          //     original: annotation.selectedText,
          //     startIndex,
          //     endIndex,
          //   });
          //   this.findAndMarkTextFallback(annotation.selectedText, annotationId, index);
          //   return;
          // }

          // 索引和文字都驗證通過，直接套用 <mark>
          this.applyMarkByIndex(bodyElement.$, startIndex, endIndex, annotationId, index);
        } catch (error) {
          // console.error(`標記批改 ${index + 1} 時發生錯誤:`, error);
          this.findAndMarkTextFallback(annotation.selectedText, annotationId, index);
        }
      });

      this.editorData = this.editorInstance.getData();

      if (wasReadOnly) {
        this.editorInstance.setReadOnly(true);
      }
    },

    /**
     * applyMarkByIndex
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：直接操作原生 DOM，將 [startIndex, endIndex) 範圍內的文字
     *       用 <mark> 元素包裹，支援跨段落（跨 block 元素）的情況。
     *
     * 【原理】
     *   1. 用 TreeWalker 依序走訪 bodyDom 下所有文字節點（TEXT_NODE）。
     *   2. 用累加 currentIndex 模擬「純文字全域偏移量」，對應 getText() 的結果。
     *   3. 找出與目標範圍 [startIndex, endIndex) 有交集的節點，記錄其局部偏移量。
     *   4. 從後往前依序切割文字節點並包裹 <mark>（倒序處理避免前面節點的 DOM
     *      參考因 splitText 而失效）。
     *
     * 【splitText 切割示意】
     *   原始節點: "ABCDE"，需標記 [1, 4) → "BCD"
     *   Step 1: splitText(4) → 節點變成 "ABCD"，後面產生新節點 "E"
     *   Step 2: splitText(1) → 原節點變成 "A"，targetNode 成為 "BCD"
     *   Step 3: insertBefore(<mark>, "BCD") + mark.appendChild("BCD")
     *   結果: "A" + <mark>BCD</mark> + "E"
     *
     * @param {Node}   bodyDom        - CKEditor body 元素的原生 DOM 節點（bodyElement.$）
     * @param {number} startIndex     - 批改文字在純文字中的起始偏移量（含）
     * @param {number} endIndex       - 批改文字在純文字中的結束偏移量（不含）
     * @param {string} annotationId   - 批改記錄的唯一 ID，寫入 data-annotation-id
     * @param {number} annotationIndex - 批改在 annotations 陣列中的索引，寫入 data-annotation-index
     */
    applyMarkByIndex(bodyDom, startIndex, endIndex, annotationId, annotationIndex) {
      const hiddenClass = this.showAnnotationHighlight ? '' : ' hidden';

      // 建立 TreeWalker，只走訪文字節點（NodeFilter.SHOW_TEXT = 4）
      const treeWalker = bodyDom.ownerDocument.createTreeWalker(
        bodyDom,
        NodeFilter.SHOW_TEXT,
        null,
        false,
      );

      // ── 第一步：收集所有與目標範圍有交集的文字節點片段 ──────────────────────
      // fragments 陣列每個元素代表「需要被包裹 <mark> 的文字節點局部片段」
      // 結構：{ node: TextNode, localStart: number, localEnd: number }
      //   - localStart：在此節點內，mark 應從哪個字元開始（相對於節點起始）
      //   - localEnd  ：在此節點內，mark 應到哪個字元結束（相對於節點起始）
      const fragments = [];
      let currentIndex = 0;
      let node = treeWalker.nextNode();

      while (node) {
        const nodeLen = node.textContent.length;
        // 此節點在純文字中的起始全域偏移
        const nodeStart = currentIndex;
        // 此節點在純文字中的結束全域偏移
        const nodeEnd = currentIndex + nodeLen;

        // 判斷此節點是否與目標範圍 [startIndex, endIndex) 有交集
        if (nodeEnd > startIndex && nodeStart < endIndex) {
          fragments.push({
            node,
            localStart: Math.max(startIndex - nodeStart, 0), // 不得小於節點起始
            localEnd: Math.min(endIndex - nodeStart, nodeLen), // 不得超過節點長度
          });
        }

        currentIndex += nodeLen;

        // 已超過目標範圍，不需再繼續走訪
        if (currentIndex >= endIndex) break;

        node = treeWalker.nextNode();
      }

      if (fragments.length === 0) {
        // console.warn('applyMarkByIndex: 在 DOM 中找不到對應的文字節點，標記失敗');
        return;
      }

      // ── 第二步：從後往前處理每個片段，切割並包裹 <mark> ────────────────────
      // 倒序處理的原因：splitText() 會產生新節點並改變 DOM 結構，
      // 若從前往後處理，前面節點被切割後其 DOM 位置會改變，
      // 導致後面尚未處理的節點參考失效。倒序則不影響尚未處理的前面節點。
      for (let i = fragments.length - 1; i >= 0; i -= 1) {
        const { node: textNode, localStart, localEnd } = fragments[i];
        const doc = textNode.ownerDocument;

        // 建立 <mark> 元素並設定必要屬性
        const mark = doc.createElement('mark');
        mark.className = `annotation-highlight${hiddenClass}`;
        mark.setAttribute('data-annotation-id', annotationId);
        mark.setAttribute('data-annotation-index', annotationIndex);

        // 切割文字節點，將目標片段 [localStart, localEnd) 獨立出來
        let targetNode = textNode;

        // ① 先切割後半段：localEnd 之後的文字獨立為新節點（本次不處理）
        if (localEnd < targetNode.textContent.length) {
          targetNode.splitText(localEnd);
          // 此時 targetNode 仍代表 [0, localEnd) 的部分
        }

        // ② 再切割前半段：localStart 之前的文字獨立為舊節點，targetNode 成為目標片段
        if (localStart > 0) {
          targetNode = targetNode.splitText(localStart);
          // 此時 targetNode 代表 [localStart, localEnd) 的部分，即需要標記的文字
        }

        // ③ 將 <mark> 插入到目標文字節點之前，再將文字節點移入 <mark> 內
        const parent = targetNode.parentNode;
        parent.insertBefore(mark, targetNode);
        mark.appendChild(targetNode);
      }
    },
    findAndMarkTextFallback(text, annotationId, annotationIndex) {
      if (!this.editorInstance || !text || text.trim() === '') return;
      try {
        let data = this.editorInstance.getData();

        // 使用正則表達式找到第一個未標記的匹配文字
        const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?!<mark[^>]*>)(${escapedText})(?![^<]*<\\/mark>)`);

        if (regex.test(data)) {
          const hiddenClass = this.showAnnotationHighlight ? '' : ' hidden';
          const markedText = `<mark class="annotation-highlight${hiddenClass}" data-annotation-id="${annotationId}" data-annotation-index="${annotationIndex}">${text}</mark>`;
          data = data.replace(regex, markedText);
          this.editorInstance.setData(data);
          this.editorData = data;
        }
      } catch (error) {
        // console.error('文字匹配標記失敗:', error);
      }
    },
    toggleAnnotationHighlight() {
      this.showAnnotationHighlight = !this.showAnnotationHighlight;
      if (!this.editorInstance) return;

      // 暫時解除唯讀狀態以應用批改標記
      const wasReadOnly = this.editorInstance.readOnly;
      if (wasReadOnly) {
        this.editorInstance.setReadOnly(false);
      }

      let data = this.editorInstance.getData();

      if (this.showAnnotationHighlight) {
        // 顯示批改標記 - 移除 hidden class
        data = data.replace(/class="annotation-highlight hidden"/g, 'class="annotation-highlight"');
      } else {
        // 隱藏批改標記 - 新增 hidden class
        data = data.replace(
          /class="annotation-highlight"(?! hidden)/g,
          'class="annotation-highlight hidden"',
        );
      }

      this.editorInstance.setData(data);
      this.editorData = data;

      // 恢復唯讀狀態
      if (wasReadOnly) {
        this.editorInstance.setReadOnly(true);
      }

      this.$message.success(this.showAnnotationHighlight ? '批改標記已顯示' : '批改標記已隱藏');
    },
    /**
     * jumpToAnnotation
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：點擊側欄批改清單時，自動捲動到該批改在編輯器中的位置，並閃爍高亮。
     * 【CKEditor 4 iframe 捲動原理】
     *
     *   ┌── 宿主頁面 DOM ─────────────────────────────────┐
     *   │  .cke_contents  (overflow: auto, 有高度限制)    │
     *   │  └── <iframe class="cke_wysiwyg_frame">         │
     *   │       └── iframe.contentDocument               │
     *   │            └── <html>                          │
     *   │                 └── <body> (editableElement)   │
     *   │                      └── <p>...<mark>...</p>   │
     *   │                                                 │
     *   └─────────────────────────────────────────────────┘
     *
     *   捲軸真正所在：iframe 的 contentDocument.documentElement（即 <html>）
     *   捲動方式：iframe.contentDocument.documentElement.scrollTop = targetScrollTop
     *
     *   targetScrollTop 計算：
     *     用 offsetTop 遞迴累加到 offsetParent 為止（即 <body>），
     *     取得 mark 元素距離 iframe body 頂端的絕對距離，
     *     再減去 iframe 可視高度的一半，使目標置中顯示。
     *
     * 【完整流程】
     *  1. querySelectorAll 取得此 annotationId 的所有 <mark> fragment
     *  2. 計算第一個 fragment 在 iframe 內的 offsetTop（累計至 body）
     *  3. 對 iframe.contentDocument.documentElement 設定 scrollTop（CKEditor 捲軸）
     *  4. 對所有 fragment 加上 .annotation-jump-active class（CSS 高亮動畫）
     *  5. 1000ms 後移除 .annotation-jump-active，還原原本樣式
     *  6. 若非唯讀，額外用 CKEditor range.select() 設定游標位置
     *
     * @param {Object} annotation - 批改物件（含 studentEssayAnswerAnnotationId）
     */
    jumpToAnnotation(annotation) {
      if (!this.editorInstance) return;

      const annotationId = annotation.studentEssayAnswerAnnotationId;
      if (!annotationId) {
        this.$message.warning('無法定位到此批改');
        return;
      }

      try {
        // 獲取編輯器的可編輯區域
        const editable = this.editorInstance.editable();
        const editableElement = editable.$;

        // ── 取得此批改的所有 <mark> fragment ────────────────────────────────
        // 跨段落批改會產生多個 <mark>（每個 <p> 各一個），皆有相同 data-annotation-id。
        // querySelectorAll 確保全部都被選到。
        const markElements = Array.from(
          editableElement.querySelectorAll(`mark[data-annotation-id="${annotationId}"]`),
        );

        if (markElements.length === 0) {
          this.$message.warning('找不到對應的批改標記');
          return;
        }

        // ── 捲動 CKEditor 內部捲軸到第一個 fragment（批改起點） ─────────────
        //   CKEditor 的捲軸由 iframe contentDocument 的 <html> 元素控制，
        //   因此對 iframe.contentDocument.documentElement.scrollTop 賦值，
        //   即可捲動 CKEditor 自身的捲軸。

        // iframe 內的 document
        const iframeDoc = this.editorInstance.document.$;
        // iframe 內的 <html>（捲動容器）
        const iframeHtml = iframeDoc.documentElement;

        // 計算 mark 元素距離 iframe body 頂端的累計 offsetTop
        // （需遞迴累加 offsetParent 鏈，以處理巢狀結構）
        let offsetTop = 0;
        let el = markElements[0];
        while (el && el !== iframeDoc.body) {
          offsetTop += el.offsetTop;
          el = el.offsetParent;
        }

        // 置中顯示：scrollTop = 元素頂端 - iframe 可視高度的一半 + 元素自身高度的一半
        const iframeViewHeight = iframeHtml.clientHeight;
        const markHeight = markElements[0].offsetHeight;
        const targetScrollTop = offsetTop - (iframeViewHeight / 2) + (markHeight / 2);

        iframeHtml.scrollTop = Math.max(0, targetScrollTop);

        // ── 對所有 fragment 套用跳轉高亮動畫 ──────────────────────────────
        markElements.forEach((el2) => el2.classList.add('annotation-jump-active'));

        // 1000ms 後移除高亮 class，還原原本的 .annotation-highlight 樣式
        setTimeout(() => {
          markElements.forEach((el2) => el2.classList.remove('annotation-jump-active'));
        }, 1000);

        // ── 設定游標位置（僅限非唯讀模式） ────────────────────────────────
        // read-only 模式下 range.select() 可能拋錯，故以 readOnly 做守衛。
        if (!this.editorInstance.readOnly) {
          const range = this.editorInstance.createRange();
          const CKEditorDomElement = window.CKEDITOR.dom.element;
          range.moveToElementEditablePosition(
            new CKEditorDomElement(markElements[0]),
            true,
          );
          range.select();
        }

        this.editorInstance.focus();
      } catch (error) {
        this.editorInstance.focus();
        this.$message.warning('定位批改位置失敗');
      }
    },
    editHTML(showInComponent = true) {
      if (this.editorMode !== 'admin') {
        this.$message.warning('此功能僅限管理者模式使用！');
        return undefined;
      }
      const htmlContentValue = this.editorData;
      if (showInComponent) {
        this.htmlContent = htmlContentValue;
        this.htmlDialogVisible = true;
      }
      this.$emit('html-data', htmlContentValue);
      return htmlContentValue;
    },
    confirmHTMLEdit() {
      if (this.editorMode !== 'admin') {
        this.$message.error('此功能僅限管理者模式使用！');
        this.htmlDialogVisible = false;
        return;
      }
      if (!this.htmlContent || this.htmlContent.trim() === '') {
        this.$message.warning('請輸入HTML內容！');
        return;
      }
      this.editorData = this.htmlContent.trim();
      if (this.editorInstance) this.editorInstance.setData(this.editorData);
      this.$emit('data-changed', { html: this.editorData });
      this.htmlDialogVisible = false;
      this.$message.success('HTML內容已更新！');
    },
    // 剪貼簿操作方法
    cutText() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('cut');
    },
    copyText() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('copy');
    },
    pasteText() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('paste');
    },
    // 復原/重做方法
    undo() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('undo');
      this.$nextTick(() => {
        this.updateButtonStates();
      });
    },
    redo() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('redo');
      this.$nextTick(() => {
        this.updateButtonStates();
      });
    },
    // 列表方法
    insertBulletedList() {
      if (!this.editorInstance) return;
      this.editorInstance.execCommand('bulletedlist');
      this.editorInstance.focus();
    },
    insertNumberedList() {
      if (!this.editorInstance) return;

      // 執行標準的創建列表命令
      // afterCommandExec 事件監聽器會自動應用樣式
      this.editorInstance.execCommand('numberedlist');
      this.editorInstance.focus();

      // 更新按鈕狀態
      this.$nextTick(() => {
        this.updateButtonStates();
      });
    },
    // 缩排方法
    increaseIndent() {
      if (!this.editorInstance) return;

      // 檢查層級限制
      if (this.enableListDepthLimit) {
        const selection = this.editorInstance.getSelection();
        if (selection) {
          const startElement = selection.getStartElement();
          // 找到當前的 li 元素
          const currentLi = startElement.getAscendant('li', true);

          if (currentLi) {
            // 計算當前 li 所在的列表深度
            let depth = 0;
            let element = currentLi.getParent(); // 從 li 的父元素（ol/ul）開始

            while (element) {
              const tagName = element.getName();
              if (tagName === 'ol' || tagName === 'ul') {
                depth += 1;
              }
              element = element.getParent();
            }

            // 如果已經是第4層，不允許再縮進到第5層
            if (depth >= 4) {
              this.$message.warning('已達到最大層級限制（4層）');
              return;
            }
          }
        }
      }

      // 確保命令可用
      const command = this.editorInstance.getCommand('indent');
      if (command && command.state !== window.CKEDITOR.TRISTATE_DISABLED) {
        this.editorInstance.execCommand('indent');
        this.editorInstance.focus();

        // 更新按鈕狀態
        this.$nextTick(() => {
          this.updateButtonStates();
        });
      }
    },
    decreaseIndent() {
      if (!this.editorInstance) return;

      // 確保命令可用
      const command = this.editorInstance.getCommand('outdent');
      if (command && command.state !== window.CKEDITOR.TRISTATE_DISABLED) {
        this.editorInstance.execCommand('outdent');
        this.editorInstance.focus();

        // 更新按鈕狀態
        this.$nextTick(() => {
          this.updateButtonStates();
        });
      }
    },
    toggleAnnotations() {
      this.isAnnotationsCollapsed = !this.isAnnotationsCollapsed;
    },
    editAnnotation(annotation) {
      this.selectedTextForAnnotation = annotation.selectedText;
      this.annotationComment = annotation.teacherComment;
      this.editingAnnotationIndex = this.annotations.indexOf(annotation);
      this.annotationDialogVisible = true;
    },
    handleContextMenu(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!this.editorInstance) {
        return false;
      }

      // 檢測是否在列表中
      const listElement = this.checkIfInList();
      this.isInListContext = !!listElement;

      // 設定目前列表類型
      if (listElement) {
        this.currentListType = listElement.getName();
      }

      const selection = this.editorInstance.getSelection();
      if (selection && selection.getRanges().length > 0) {
        const [firstRange] = selection.getRanges();
        this.savedSelection = firstRange;
      }

      // 計算右鍵選單位置，考慮視窗邊界
      const menuWidth = 200; // 預估選單寬度
      const menuHeight = 300; // 預估選單高度
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // 獲取 CKEditor 容器相對於視窗的位置
      const editable = this.editorInstance.editable();
      const editableElement = editable.$;

      // 計算選單位置
      let x = event.clientX || event.pageX || 0;
      let y = event.clientY || event.pageY || 0;

      // 如果編輯器在 iframe 中，需要調整座標
      if (editableElement.ownerDocument !== document) {
        // 在 iframe 中，找到實際的 iframe 元素
        const iframes = document.getElementsByTagName('iframe');
        for (let i = 0; i < iframes.length; i += 1) {
          const iframe = iframes[i];
          try {
            if (
              iframe.contentDocument === editableElement.ownerDocument
              || iframe.contentWindow.document === editableElement.ownerDocument
            ) {
              const rect = iframe.getBoundingClientRect();
              x += rect.left;
              y += rect.top;
              break;
            }
          } catch (e) {
            // 跨域 iframe，忽略
          }
        }
      }

      // 檢查是否超出右邊界
      if (x + menuWidth > windowWidth) {
        x = windowWidth - menuWidth - 10;
      }

      // 檢查是否超出下邊界
      if (y + menuHeight > windowHeight) {
        y = windowHeight - menuHeight - 10;
      }

      // 確保不會超出左邊和上邊
      x = Math.max(10, x);
      y = Math.max(10, y);

      this.contextMenuX = x;
      this.contextMenuY = y;
      this.contextMenuVisible = true;

      // 在下一個事件循環中新增編輯器內部的點擊監聽器
      this.$nextTick(() => {
        if (this.editorInstance) {
          const editable3 = this.editorInstance.editable();
          if (editable3 && editable3.$) {
            const editableDoc = editable3.$.ownerDocument;
            // 移除舊的監聽器（如果存在）
            editableDoc.removeEventListener('click', this.hideContextMenu, true);
            // 新增監聽器（捕獲階段）
            editableDoc.addEventListener('click', this.hideContextMenu, true);
          }
        }
      });

      return false;
    },
    checkIfInList() {
      if (!this.editorInstance) return null;
      const selection = this.editorInstance.getSelection();
      if (!selection) return null;

      const startElement = selection.getStartElement();
      if (!startElement) return null;

      // 向上遍歷查找列表元素
      let element = startElement;
      while (element) {
        const tagName = element.getName();
        if (tagName === 'ol' || tagName === 'ul') {
          return element;
        }
        element = element.getParent();
      }
      return null;
    },
    openListPropertiesDialog() {
      this.hideContextMenu();

      // 恢復之前儲存的選取範圍
      if (this.savedSelection && this.editorInstance) {
        const selection = this.editorInstance.getSelection();
        selection.selectRanges([this.savedSelection]);
      }

      this.currentListElement = this.checkIfInList();

      if (this.currentListElement) {
        const tagName = this.currentListElement.getName();
        this.currentListType = tagName;

        // 檢查是否有自訂樣式的 class
        if (this.currentListElement.hasClass('parentheses-list')) {
          this.listStyle = 'parentheses';
        } else if (this.currentListElement.hasClass('parentheses-chinese-list')) {
          this.listStyle = 'parentheses-chinese';
        } else if (this.currentListElement.hasClass('cjk-ideographic-list')) {
          this.listStyle = 'cjk-ideographic';
        } else {
          // 取得目前樣式
          const currentStyle = this.currentListElement.getStyle('list-style-type');
          this.listStyle = currentStyle || (tagName === 'ol' ? 'decimal' : 'disc');
        }

        // 取得目前起始序號（僅有序列表）
        if (tagName === 'ol') {
          const currentStart = this.currentListElement.getAttribute('start');
          this.listStartIndex = currentStart ? parseInt(currentStart, 10) : 1;
        }

        this.listPropertiesDialogVisible = true;
      } else {
        this.$message.warning('請將游標放在列表中');
      }
    },
    confirmSetListProperties() {
      if (!this.currentListElement) return;

      // 設置起始序號（僅有序列表）
      if (this.currentListType === 'ol' && this.listStartIndex >= 1) {
        this.currentListElement.setAttribute('start', this.listStartIndex);
      }

      // 自訂樣式查找表（CSS class 由 CSS counter 控制，不使用 list-style-type）
      const customStyleMap = {
        parentheses: 'parentheses-list',
        'parentheses-chinese': 'parentheses-chinese-list',
        'cjk-ideographic': 'cjk-ideographic-list',
      };
      const allCustomClasses = Object.values(customStyleMap);
      const selectedClass = customStyleMap[this.listStyle];
      // counter-reset 的值需要比實際起始值小 1
      const counterValue = this.listStartIndex - 1;

      // 統一清除所有自訂 class，避免樣式殘留
      allCustomClasses.forEach((cls) => this.currentListElement.removeClass(cls));

      if (selectedClass) {
        // 套用自訂樣式
        this.currentListElement.addClass(selectedClass);
        this.currentListElement.removeStyle('list-style-type');
        this.currentListElement.setStyle('counter-reset', `item ${counterValue}`);
      } else {
        // 套用標準樣式
        this.currentListElement.setStyle('list-style-type', this.listStyle);
        if (this.currentListType === 'ol') {
          // 有序列表需要 counter-reset 以支援自訂起始序號
          this.currentListElement.setStyle('counter-reset', `item ${counterValue}`);
        } else {
          // 無序列表不需要 counter-reset
          this.currentListElement.removeStyle('counter-reset');
        }
      }

      // ⭐ 移除自動樣式標記，表示這是用戶手動設置的
      this.currentListElement.removeAttribute('data-auto-style');

      // 更新編輯器資料
      this.editorData = this.editorInstance.getData();
      this.$emit('data-changed', { html: this.editorData });

      this.listPropertiesDialogVisible = false;
    },
    hideContextMenu(event) {
      // 如果點擊的是右鍵選單本身，不關閉
      if (event && event.target) {
        const contextMenu = event.target.closest('.context-menu');
        if (contextMenu) {
          return;
        }
      }
      this.contextMenuVisible = false;
    },
    selectAllText() {
      if (!this.editorInstance) return;
      this.hideContextMenu();

      // 使用多種方式確保全選功能正常
      this.$nextTick(() => {
        try {
          // 方法1: 使用 CKEditor 的選取 API
          const editable = this.editorInstance.editable();
          const range = this.editorInstance.createRange();
          range.selectNodeContents(editable);

          const selection = this.editorInstance.getSelection();
          selection.selectRanges([range]);

          // 方法2: 備用方案 - 使用 execCommand
          if (!selection.getSelectedText()) {
            this.editorInstance.execCommand('selectAll');
          }

          this.editorInstance.focus();
        } catch (error) {
          // console.error('全選失敗:', error);
          // 最後備用方案
          this.editorInstance.execCommand('selectAll');
        }
      });
    },
    addAnnotationFromContextMenu() {
      this.hideContextMenu();
      if (this.savedSelection && this.editorInstance) {
        const selection = this.editorInstance.getSelection();
        selection.selectRanges([this.savedSelection]);
      }
      this.addAnnotation();
    },
    cutTextFromContextMenu() {
      this.hideContextMenu();
      this.$nextTick(() => { this.cutText(); });
    },
    copyTextFromContextMenu() {
      this.hideContextMenu();
      this.$nextTick(() => { this.copyText(); });
    },
    pasteTextFromContextMenu() {
      this.hideContextMenu();
      this.$nextTick(() => { this.pasteText(); });
    },
    handleKeyboardShortcut(event) {
      // 處理 Ctrl+M 快捷鍵
      if ((event.ctrlKey || event.metaKey) && event.key === 'm') {
        event.preventDefault();
        // 在批改模式或管理者模式下允許使用 Ctrl+M
        if (this.editorMode === 'review' || this.editorMode === 'admin') {
          this.addAnnotation();
        } else {
          this.$message.warning('批改功能僅在批改模式或管理者模式下可用！');
        }
      }
    },
    getPreviewStyle() {
      // 為自訂樣式設定 counter-reset
      if (
        this.listStyle === 'parentheses'
        || this.listStyle === 'parentheses-chinese'
        || this.listStyle === 'cjk-ideographic'
      ) {
        const counterValue = this.listStartIndex - 1;
        return `counter-reset: item ${counterValue}; padding-left: 2em;`;
      }

      // 標準數字列表也需要設置 counter-reset 以支持自定義起始序號
      if (this.currentListType === 'ol') {
        const counterValue = this.listStartIndex - 1;
        return `list-style-type: ${this.listStyle}; counter-reset: item ${counterValue}; padding-left: 2em;`;
      }

      return `list-style-type: ${this.listStyle}; padding-left: 2em;`;
    },
    // 切換稿紙格線
    toggleNoteLines() {
      this.showNoteLines = !this.showNoteLines;

      if (this.editorInstance) {
        // 動態切換 body class
        const body = this.editorInstance.document.getBody();
        if (this.showNoteLines) {
          body.removeClass('note-lines-hidden');
          body.addClass('note-lines');
        } else {
          body.removeClass('note-lines');
          body.addClass('note-lines-hidden');
        }
      }

      this.$message.success(
        this.showNoteLines ? '已顯示稿紙格線' : '已隱藏稿紙格線',
      );
    },
    // 切換列表層級限制
    toggleListDepthLimit() {
      this.enableListDepthLimit = !this.enableListDepthLimit;

      this.$message.success(
        this.enableListDepthLimit
          ? '已開啟層級限制（最多4層）'
          : '已關閉層級限制（允許無限層級）',
      );
    },
    /**
     * updatePageMarkers
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：動態更新頁碼標記，在編輯器內容區每26行（936px）顯示分隔線和頁碼。
     *
     * 實現原理：
     * 1. 強制移除所有舊的頁碼標記元素（包括可能殘留的）
     * 2. 使用 requestAnimationFrame 確保 DOM 更新完成
     * 3. 獲取編輯器內容區的實際高度（scrollHeight，不含頁碼標記）
     * 4. 根據每頁高度（26行 × 36px = 936px）計算總頁數
     * 5. 在每頁邊界處動態插入分隔線和頁碼標籤
     * 6. 頁碼標籤顯示在編輯器右側外部
     */
    updatePageMarkers() {
      if (!this.editorInstance) return;

      try {
        const editable = this.editorInstance.editable();
        const editableElement = editable.$;

        // 獲取編輯器內的 document
        const { ownerDocument: doc } = editableElement;
        const { body } = doc;

        // ⭐ 關鍵修改 1：強制移除所有頁碼標記（包括可能殘留的）
        const oldMarkers = body.querySelectorAll('.page-marker');
        oldMarkers.forEach((marker) => marker.remove());

        // ⭐ 關鍵修改 2：使用 requestAnimationFrame 確保 DOM 更新完成
        // 這樣可以確保在計算 scrollHeight 時，舊的頁碼標記已經完全移除
        requestAnimationFrame(() => {
          // 定義常數
          const lineHeight = 36; // 每行高度 px
          const linesPerPage = 26; // 每頁行數
          const pageHeight = lineHeight * linesPerPage; // 936px

          // ⭐ 此時 scrollHeight 是乾淨的（不含舊頁碼標記）
          const { scrollHeight: contentHeight } = body;

          // 計算總頁數：扣除 CURSOR_TOLERANCE 來吸收游標/padding 在頁面邊界造成的微小 overflow
          // 游標 overflow 通常僅 1-5px；實際新頁面第一行至少帶來 12px+ 的 overflow
          const CURSOR_TOLERANCE = 6; // px
          const totalPages = Math.max(
            1,
            Math.ceil((contentHeight - CURSOR_TOLERANCE) / pageHeight),
          );

          // 檢查頁數警告（不在初始化階段觸發）
          if (this.isPageMarkersInitialized && this.editorMode === 'answer') {
            // 「已達最大頁數」警告：剛好 = 最大頁數才顯示（超過最大頁數時只顯示超限警告，避免兩個警告同時跳出）
            if (totalPages === this.CurrentQuestionLimitPage && this.CurrentQuestionLimitPage > 1) {
              if (!this.hasShownPageLimitWarning) {
                this.hasShownPageLimitWarning = true;
                this.$alert(
                  '已達最末頁，請注意作答範圍。',
                  '提醒',
                  {
                    confirmButtonText: '我知道了',
                    type: 'warning',
                  },
                );
              }
            } else if (totalPages < this.CurrentQuestionLimitPage) {
              // 頁數降回最大頁數限制以下，重置旗標
              this.hasShownPageLimitWarning = false;
            }

            // 超過最大頁數限制警告
            if (totalPages > this.CurrentQuestionLimitPage) {
              if (!this.hasShownPageExceededWarning) {
                this.hasShownPageExceededWarning = true;
                this.$alert(
                  `內容超過${this.CurrentQuestionLimitPage}頁，請調整內容。`,
                  '頁數過多警告',
                  {
                    confirmButtonText: '我知道了',
                    type: 'warning',
                  },
                );
              }
            } else {
              // 頁數降回最大頁數限制以下，重置旗標
              this.hasShownPageExceededWarning = false;
            }
          }

          // 如果內容不足一頁，不需要顯示頁碼
          if (totalPages <= 1) return;

          // 為每個頁面邊界插入標記（從第2頁開始）
          for (let pageNum = 2; pageNum <= totalPages; pageNum += 1) {
            const marker = doc.createElement('div');
            marker.className = 'page-marker';

            // 計算分隔線位置：第 pageNum 頁的起始位置
            const topPosition = (pageNum - 1) * pageHeight;
            marker.style.top = `${topPosition}px`;

            // 創建分隔線元素
            const line = doc.createElement('div');
            line.className = 'page-line';
            marker.appendChild(line);

            // 創建頁碼標籤元素
            const pageLabel = doc.createElement('div');
            pageLabel.className = 'page-number';
            pageLabel.textContent = `第 ${pageNum} 頁`;
            marker.appendChild(pageLabel);

            // 將標記添加到 body
            body.appendChild(marker);
          }
        });
      } catch (error) {
        // console.error('更新頁碼標記失敗:', error);
      }
    },
    /**
     * schedulePageMarkersUpdate
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：使用防抖機制延遲更新頁碼標記，避免頻繁更新影響性能。
     *
     * 在用戶輸入內容時，不會立即更新頁碼，而是等待 300ms 無操作後才更新。
     */
    schedulePageMarkersUpdate() {
      // 清除現有的計時器
      if (this.pageMarkersUpdateTimer) {
        clearTimeout(this.pageMarkersUpdateTimer);
      }

      // 設置新的計時器，300ms 後更新
      this.pageMarkersUpdateTimer = setTimeout(() => {
        this.updatePageMarkers();
      }, 300);
    },
    /**
     * getCleanHTML
     * ─────────────────────────────────────────────────────────────────────────
     * 功能：獲取不含頁碼標記的乾淨 HTML 內容。
     *
     * 實現原理：
     * 1. 暫時移除所有 .page-marker 元素
     * 2. 獲取編輯器的 HTML 內容（getData）
     * 3. 恢復頁碼標記元素（僅供顯示用）
     * 4. 返回乾淨的 HTML
     *
     * 這確保了頁碼標記只在編輯器中顯示，不會被儲存到資料庫。
     */
    getCleanHTML() {
      if (!this.editorInstance) return '';

      try {
        // 直接取得 HTML 字串，不操作編輯器 DOM
        // 避免 DOM 修改觸發 CKEditor change 事件，也解決多頁碼標記還原順序問題
        const rawHTML = this.editorInstance.getData();
        if (!rawHTML || rawHTML.trim() === '') return rawHTML;

        // 使用 DOMParser 在沙盒 DOM 中移除頁碼標記，不影響編輯器實際 DOM
        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHTML, 'text/html');
        doc.querySelectorAll('.page-marker').forEach((el) => el.remove());
        return doc.body.innerHTML;
      } catch (error) {
        // console.error('獲取乾淨 HTML 失敗:', error);
        // 如果失敗，返回原始內容
        return this.editorInstance.getData();
      }
    },
    // 檢查列表是否有使用者自訂樣式
    hasUserDefinedStyle(listElement) {
      if (!listElement) return false;

      // 如果有 data-auto-style 屬性，表示是系統自動設定的
      if (listElement.getAttribute('data-auto-style') === 'true') {
        return false;
      }

      // 檢查是否有任何樣式類別或 list-style-type
      const hasClass = listElement.hasClass('cjk-ideographic-list')
                       || listElement.hasClass('parentheses-chinese-list')
                       || listElement.hasClass('parentheses-list');

      const hasStyleType = listElement.getStyle('list-style-type');

      // 有樣式但沒有 data-auto-style 標記 = 使用者手動設定
      return (hasClass || hasStyleType)
             && listElement.getAttribute('data-auto-style') !== 'true';
    },
    // 計算列表深度
    calculateDepth(listElement) {
      let depth = 0;
      let parent = listElement.getParent();

      while (parent) {
        const tagName = parent.getName();
        if (tagName === 'ol' || tagName === 'ul') {
          depth += 1;
        }
        parent = parent.getParent();
      }

      return depth;
    },
    // 根據深度套用樣式
    applyStyleByDepth(listElement, depth, markAsAuto = false) {
      const listStyleMap = {
        0: 'cjk-ideographic-list', // 第1層：一、
        1: 'parentheses-chinese-list', // 第2層：(一)
        2: 'decimal', // 第3層：1、
        3: 'parentheses-list', // 第4層：(1)
      };

      // 移除所有樣式類別
      Object.values(listStyleMap).forEach((className) => {
        if (className !== 'decimal') {
          listElement.removeClass(className);
        }
      });

      // 套用對應層級的樣式
      if (depth < 4) {
        const styleClass = listStyleMap[depth];
        if (styleClass === 'decimal') {
          listElement.setStyle('list-style-type', 'decimal');
          listElement.setStyle('counter-reset', 'item 0');
        } else {
          listElement.addClass(styleClass);
          listElement.removeStyle('list-style-type');
          listElement.setStyle('counter-reset', 'item 0');
        }
      } else if (!this.enableListDepthLimit) {
        // 第5層及之後：如果關閉限制，使用 decimal
        listElement.setStyle('list-style-type', 'decimal');
        listElement.setStyle('counter-reset', 'item 0');
      }

      // 標記為自動樣式
      if (markAsAuto) {
        listElement.setAttribute('data-auto-style', 'true');
      }
    },
    // 將列表調整到最大深度
    adjustListToMaxDepth(listElement, maxDepth) {
      if (!listElement) return;

      // 計算目前列表的深度
      const currentDepth = this.calculateDepth(listElement);

      // 如果深度在允許範圍內，不需要調整
      if (currentDepth <= maxDepth) return;

      // 找到此列表的父 li 元素
      const parentLi = listElement.getParent();
      if (!parentLi || parentLi.getName() !== 'li') return;

      // 找到父 li 所在的父列表
      const parentList = parentLi.getParent();
      if (!parentList || (parentList.getName() !== 'ol' && parentList.getName() !== 'ul')) return;

      // 取得目前列表的所有直接子 li 元素
      const listItems = listElement.getChildren();
      const itemsToMove = [];

      for (let i = 0; i < listItems.count(); i += 1) {
        const item = listItems.getItem(i);
        if (item.getName() === 'li') {
          itemsToMove.push(item);
        }
      }

      // 將所有子 li 移到父列表中，插入到父 li 之後
      itemsToMove.reverse().forEach((item) => {
        // 複製元素以保留內容
        const clonedItem = item;
        // 將元素插入到父 li 之後
        clonedItem.insertAfter(parentLi);
      });

      // 移除空的超限列表
      if (listElement.getChildren().count() === 0) {
        listElement.remove();
      }
    },
    // 更新按鈕狀態
    updateButtonStates() {
      if (!this.editorInstance) return;

      try {
        const selection = this.editorInstance.getSelection();

        // 更新選取狀態 - 使用多種方式檢測
        let hasText = false;
        if (selection) {
          const selectedText = selection.getSelectedText();
          // 檢查是否有選取的文字
          hasText = selectedText && selectedText.trim().length > 0;

          // 如果上面的方法不行，嘗試使用 ranges
          if (!hasText) {
            const ranges = selection.getRanges();
            if (ranges && ranges.length > 0) {
              const [firstRange] = ranges;
              // 檢查範圍是否為空
              hasText = !firstRange.collapsed;
            }
          }
        }

        this.hasSelection = hasText;

        // 檢查是否在列表中以及縮排狀態
        const path = this.editorInstance.elementPath();
        let canIncreaseIndent = false;
        let listDepth = 0;

        if (path && path.elements) {
          // 找到目前的列表項（li）
          let currentLi = null;
          let parentList = null;

          for (let i = 0; i < path.elements.length; i += 1) {
            const element = path.elements[i];
            const tagName = element.getName();

            if (tagName === 'li' && !currentLi) {
              currentLi = element;
            }

            if (tagName === 'ul' || tagName === 'ol') {
              if (!parentList) {
                parentList = element;
              }
              listDepth += 1;
            }
          }

          // 判斷是否可以增加縮排
          if (currentLi && parentList) {
            // 檢查目前 li 是否是列表中的第一項
            const previousSibling = currentLi.getPrevious();
            // 只有目前項不是第一項時，才能增加縮排
            canIncreaseIndent = !!previousSibling;
          }
        }

        // 使用 CKEditor 的命令狀態來判斷（這更準確）
        const indentCommand = this.editorInstance.getCommand('indent');
        if (indentCommand) {
          // TRISTATE_DISABLED = 0, TRISTATE_OFF = 1, TRISTATE_ON = 2
          this.isInList = indentCommand.state !== window.CKEDITOR.TRISTATE_DISABLED;
        } else {
          // 如果命令不存在，使用我們自己的判斷
          this.isInList = canIncreaseIndent;
        }

        // 使用 CKEditor 的命令狀態來判斷是否可以減少縮排
        const outdentCommand = this.editorInstance.getCommand('outdent');
        if (outdentCommand) {
          // TRISTATE_DISABLED = 0, TRISTATE_OFF = 1, TRISTATE_ON = 2
          this.canDecreaseIndent = outdentCommand.state !== window.CKEDITOR.TRISTATE_DISABLED;
        } else {
          // 如果命令不存在，則使用列表深度判斷
          this.canDecreaseIndent = listDepth > 1;
        }

        // 檢查復原/重做狀態
        const { undoManager } = this.editorInstance;
        if (undoManager) {
          // 確保返回值是布林值，而不是 undefined
          this.canUndo = !!undoManager.undoable();
          this.canRedo = !!undoManager.redoable();
        } else {
          this.canUndo = false;
          this.canRedo = false;
        }
      } catch (error) {
        // console.error('更新按鈕狀態時發生錯誤:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.context-menu {
  position: fixed;
  background: #fff;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 180px;
  z-index: 9999;

  .context-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #24292e;
    transition: background-color 0.2s ease;

    i {
      font-size: 16px;
      color: #586069;
    }

    &:hover {
      background-color: #f3f4f6;
    }

    &:active {
      background-color: #e1e4e8;
    }
  }

  .context-menu-divider {
    height: 1px;
    background-color: #e1e4e8;
    margin: 4px 0;
  }
}

.ckeditor-component {
  width: 100%;
  height: 100%;
}

.ckeditor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar {
  background: #fff;
  padding: 12px 16px;
  margin: 0 0 12px 0;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  button {
    margin: 0;
    padding: 6px 12px;
    border: 1px solid #d1d5da;
    border-radius: 4px;
    background: #fff;
    color: #24292e;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .toolbar-divider {
    width: 1px;
    height: 20px;
    background: #e1e4e8;
    margin: 0 4px;
  }
}

.main-content-wrapper {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
  align-items: stretch;
}

.editor-section {
  flex: 4;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.editor-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  .word-limit-alert {
    margin-bottom: 12px;
    border-radius: 4px;
    animation: alertPulse 0.5s ease-in-out;

    :deep(.el-alert__content) {
      strong {
        font-weight: 700;
        font-size: 14px;
      }
    }
  }

  @keyframes alertPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      transform: scale(1);
    }
  }

  .custom-editor {
    height: 100%;
    border: 1px solid #ddd;
    border-radius: 4px;
    transition: all 0.3s ease;

    :deep(.cke_contents) {
      user-select: text;
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
    }

    &:focus {
      border-color: #409eff;
    }

    &.limit-close {
      border-color: #e6a23c;
      border-width: 2px;
      background-color: #fdf6ec;
      box-shadow: 0 0 0 3px rgba(230, 162, 60, 0.1);
    }

    &.limit-exceeded {
      border-color: #f56c6c;
      border-width: 2px;
      background-color: #fef0f0;
      box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.15);
      animation: limitExceededShake 0.5s ease-in-out;
    }

    @keyframes limitExceededShake {
      0%, 100% {
        transform: translateX(0);
      }
      25% {
        transform: translateX(-5px);
      }
      75% {
        transform: translateX(5px);
      }
    }

    :deep(mark.annotation-highlight) {
      display: inline;
      background-color: #ffff00;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &.hidden {
        background-color: transparent !important;
        cursor: inherit;
      }
    }
  }
}

.word-count-container {
  display: flex;
  gap: 10px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  margin-bottom: 12px;
  position: relative;
  transition: all 0.3s ease;

  &.bottom {
    margin: 0;
    border-top: none;
    border-radius: 0 0 6px 6px;
  }

  .word-count-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    .count-label {
      color: #606266;
      font-weight: 500;
    }

    .count-value {
      color: #303133;
      font-weight: 600;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      transition: color 0.3s ease;

      &.text-warning {
        color: #e6a23c;
        font-size: 14px;
      }

      &.text-danger {
        color: #f56c6c;
        font-size: 14px;
        animation: textPulse 1s ease-in-out infinite;
      }
    }
  }

  button {
    margin: 0;
    padding: 6px 12px;
    border: 1px solid #d1d5da;
    border-radius: 4px;
    background: #fff;
    color: #24292e;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.btn-note-lines.active,
    &.btn-list-limit.active {
      background: #e6f3ff;
      border-color: #0366d6;
      color: #0366d6;
      font-weight: 600;
    }
  }

  @keyframes textPulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  @keyframes indicatorBlink {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }
}

.annotations-section {
  flex: 1;
  min-width: 280px;
  max-width: 380px;
  transition: all 0.3s ease;
  position: relative;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &.collapsed {
    flex: 0;
    min-width: 10px;
    max-width: 10px;
  }
}

.panel-tab {
  position: absolute;
  left: -32px;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
  background: #fff;
  color: #5d5d5d;
  border: none;
  border-radius: 6px 0 0 6px;
  padding: 12px 6px;
  cursor: pointer;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: #f2f2f2;
  }
}

.annotations-panel {
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 650px;

  .annotations-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e1e4e8;
    background: #f6f8fa;

    h6 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #24292e;
    }

    .btn-toggle-highlight {
      padding: 6px 12px;
      border: 1px solid #d1d5da;
      border-radius: 4px;
      background: #fff;
      color: #24292e;
      font-size: 13px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &:hover {
        background: #f3f4f6;
      }
    }
  }

  .annotations-list {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .annotation-item {
    padding: 14px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #e1e4e8;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    position: relative;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }

    &.dragging {
      opacity: 0.5;
    }

    &.drag-over {
      border-top: 3px solid #0366d6;
    }

    &.can-drop-above {
      cursor: move;
    }

    .annotation-number {
      position: absolute;
      top: 14px;
      left: 14px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff3cd;
      color: #856404;
      border-radius: 4px;
      font-weight: 600;
      font-size: 11px;
    }

    .annotation-content {
      padding-left: 36px;
      word-break: break-word;

      .annotation-text {
        font-size: 13px;
        margin-bottom: 8px;
        font-weight: 500;
        color: #24292e;
        padding-right: 44px;

        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .annotation-comment {
        font-size: 13px;
        color: #586069;
        white-space: pre-line;
        word-wrap: break-word;

        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .btn-edit,
    .btn-view,
    .btn-delete {
      position: absolute;
      top: 16px;
      width: 24px;
      height: 24px;
      padding: 0;
      border: none;
      border-radius: 4px;
      background: transparent;
      color: #959da5;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        color: #0366d6;
      }
    }

    .btn-edit {
      right: 36px;
    }

    .btn-delete {
      right: 8px;

      &:hover {
        color: #dc3545;
      }
    }

    .btn-view {
      right: 16px;
    }

    .annotation-move-actions {
      position: absolute;
      bottom: 8px;
      right: 8px;
      display: flex;
      gap: 4px;

      .btn-move {
        width: 24px;
        height: 24px;
        padding: 0;
        border: 1px solid #d1d5da;
        border-radius: 4px;
        background: #fff;
        color: #586069;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover:not(:disabled) {
          background: #f3f4f6;
        }

        &:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
      }
    }
  }
}

.dialog-content {
  padding: 10px 0;

  .selected-text-label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .selected-text-box {
    background: #f5f7fa;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 20px;
    font-size: 14px;
    white-space: pre-wrap;
  }

  &.list-properties-dialog {
    .form-group {
      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: #24292e;
        font-size: 14px;
      }

      .hint-text {
        font-size: 12px;
        color: #586069;
        margin-top: 6px;
      }
    }

    .preview-section {
      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: #24292e;
        font-size: 14px;
      }

      .preview-box {
        background: #f6f8fa;
        border: 1px solid #e1e4e8;
        border-radius: 6px;
        padding: 16px 20px;

        ol,
        ul {
          margin: 0;

          li {
            margin-bottom: 8px;
            line-height: 1.6;
            color: #24292e;
          }
        }

        // 標準數字列表
        ol {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;

          > li {
            counter-increment: item;
            display: flex;
            align-items: flex-start;

            &::before {
              content: counter(item) '.';
              text-align: left;
              margin-right: 0.5em;
            }
          }
        }

        // 括號數字樣式 (1)、(2)、(3)
        ol.parentheses-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;

          > li {
            counter-increment: item;
            display: flex;
            align-items: flex-start;

            &::before {
              content: '(' counter(item) ')';
              text-align: left;
              margin-right: 0.5em;
            }
          }
        }

        // 括號中文數字樣式 (一)、(二)、(三)
        ol.parentheses-chinese-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;

          > li {
            counter-increment: item;
            display: flex;
            align-items: flex-start;

            &::before {
              content: '(' counter(item, cjk-ideographic) ')';
              text-align: left;
              margin-right: 0.5em;
            }
          }
        }

        // 中文數字樣式 一、二、三
        ol.cjk-ideographic-list {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;

          > li {
            counter-increment: item;
            display: flex;
            align-items: flex-start;

            &::before {
              content: counter(item, cjk-ideographic) '、';
              text-align: left;
              margin-right: 0.5em;
            }
          }
        }

        // 無序列表
        ul {
          padding-left: 1.5em !important;
        }
      }
    }
  }
}

// 手機版響應式樣式
@media (max-width: 768px) {
  .ckeditor-wrapper {
    padding-bottom: 0;
  }

  .toolbar {
    button {
      font-size: 12px;
      padding: 5px 10px;
    }
  }

  .main-content-wrapper {
    flex-direction: column;
    padding-bottom: 160px;
  }

  .editor-section {
    flex: 1;
    width: 100%;
  }

  .editor-wrapper {
    margin-bottom: 12px;
  }

  .word-count-container {
    position: relative;
    margin-top: 0;
    margin-bottom: 15px;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .annotations-section {
    position: fixed;
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    max-width: 100%;
    min-width: 100%;
    height: auto;
    max-height: 80vh;
    z-index: 1000;
    background: #fff;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e1e4e8;
    border-bottom: none;

    &.collapsed {
      max-height: 48px;
      min-width: 100%;
      flex: none;

      &::before {
        display: none;
      }
    }
  }

  .panel-tab {
    position: relative;
    width: 100%;
    left: 0;
    top: 0;
    transform: none;
    writing-mode: horizontal-tb;
    border-radius: 16px 16px 0 0;
    padding: 12px;
    margin: 0;
    box-shadow: none;
    border-bottom: 1px solid #e1e4e8;

    .tab-text {
      writing-mode: horizontal-tb;
      text-align: center;
      width: 100%;
    }

    &::after {
      content: '▼';
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 12px;
      transition: transform 0.3s ease;
    }

    &.expanded {
      left: 0;

      &::after {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  }

  .annotations-panel {
    margin: 0;
    border: none;
    border-radius: 0;
    max-height: calc(80vh - 48px);
    overflow-y: auto;

    .annotations-list {
      padding: 12px;
    }
  }

  .annotation-item {
    flex-direction: column;
    gap: 10px;

    button {
      align-self: flex-end;
    }
  }
}
</style>

<style lang="scss">
/* 把這些自訂按鈕的 icon 拿掉，用文字代替 */
.cke_button__cjkcomma .cke_button_icon,
.cke_button__cjkperiod .cke_button_icon,
.cke_button__cjkdunhao .cke_button_icon,
.cke_button__cjksemicolon .cke_button_icon,
.cke_button__cjkcolon .cke_button_icon,
.cke_button__cjkexclam .cke_button_icon,
.cke_button__cjkquestion .cke_button_icon,
.cke_button__cjkparen .cke_button_icon,
.cke_button__cjkquote .cke_button_icon,
.cke_button__cjkdquote .cke_button_icon,
.cke_button__cjkdash .cke_button_icon,
.cke_button__circle .cke_button_icon,
.cke_button__mark .cke_button_icon,
.cke_button__sectionsign .cke_button_icon,
.cke_button__ellipsis .cke_button_icon {
  background: none !important;
  width: 16px;
  text-indent: 0;
}
.cke_button__annotation .cke_button_icon {
  background: none !important;
  width: 30px;
  text-indent: 0;
}

/* 用 :before 直接塞符號進去 */
.cke_button__cjkcomma .cke_button_icon:before {
  content: '，';
}
.cke_button__cjkperiod .cke_button_icon:before {
  content: '。';
}
.cke_button__cjkdunhao .cke_button_icon:before {
  content: '、';
}
.cke_button__cjksemicolon .cke_button_icon:before {
  content: '；';
}
.cke_button__cjkcolon .cke_button_icon:before {
  content: '：';
}
.cke_button__cjkexclam .cke_button_icon:before {
  content: '！';
}
.cke_button__cjkquestion .cke_button_icon:before {
  content: '？';
}
.cke_button__cjkparen .cke_button_icon:before {
  content: '（）';
}
.cke_button__cjkquote .cke_button_icon:before {
  content: '「」';
}
.cke_button__cjkdquote .cke_button_icon:before {
  content: '『』';
}
.cke_button__cjkdash .cke_button_icon:before {
  content: '－';
}
.cke_button__circle .cke_button_icon:before {
  content: '○';
}
.cke_button__mark .cke_button_icon:before {
  content: '※';
}
.cke_button__sectionsign .cke_button_icon:before {
  content: '§';
}
.cke_button__ellipsis .cke_button_icon:before {
  content: '…';
}
.cke_button__annotation .cke_button_icon:before {
  content: '批改';
}

.cke_button__cjkcomma .cke_button_icon:before,
.cke_button__cjkperiod .cke_button_icon:before,
.cke_button__cjkdunhao .cke_button_icon:before,
.cke_button__cjksemicolon .cke_button_icon:before,
.cke_button__cjkcolon .cke_button_icon:before,
.cke_button__cjkexclam .cke_button_icon:before,
.cke_button__cjkquestion .cke_button_icon:before,
.cke_button__cjkparen .cke_button_icon:before,
.cke_button__cjkquote .cke_button_icon:before,
.cke_button__cjkdquote .cke_button_icon:before,
.cke_button__cjkdash .cke_button_icon:before,
.cke_button__circle .cke_button_icon:before,
.cke_button__mark .cke_button_icon:before,
.cke_button__sectionsign .cke_button_icon:before,
.cke_button__ellipsis .cke_button_icon:before {
  display: inline-block;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  width: 100%;
}

.cke_button__annotation .cke_button_icon:before {
  display: inline-block;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  width: 100%;
}

.cke_chrome {
  overflow: hidden;
  border-radius: 6px 6px 0 0;
}

.page-content.note-lines {
  word-break: break-word;
}
</style>
